import Phaser from 'phaser';
import { EventBus, useGameStore, canEnterZone, canEnterRoom, canUseClaimedRoom, isWhiteboard, isNoticeBoard, doorsCatalog, findFurniture, isFloorCellBlocking, MAX_FLOOR_DIMENSION, isValidCollisionGrid, deskObstacles, standingPlacement, isScooterModel, SCOOTER_SPEED_MULTIPLIER, seatDirection, seatSpots, parseSeat, sameSeatSpot, ELEVATOR_BOARDING_MS, ELEVATOR_LANDING_SPOTS, chooseLandingSpot, cabinSpotForLanding } from '@campus/core';
import { useRoomClaimsStore } from '../../stores/roomClaimsStore.js';
import { sessionInfo } from '../../ui/people/membersStore.js';
import type { GameEventMap, ZoneConfig, Portal, ZoneAssetUrls, PlayerStateDTO, FloorLayerData, FloorLayerName, MapLabel, AvatarAppearance, ScooterModel, ScooterSnapshot, Seat, SeatDirection } from '@campus/core';
import { ZoneLoader } from '../loaders/ZoneLoader.js';
import { WallRenderer, preloadWallAtlas } from '../loaders/WallRenderer.js';
import { FloorRenderer, preloadFloorAtlas, FLOOR_DEPTHS } from '../loaders/FloorRenderer.js';
import { LabelRenderer } from '../loaders/LabelRenderer.js';
import { ensureFurnitureTextures } from '../loaders/FurnitureTextures.js';
import { registerAvatarAnimations } from '../sprites/AvatarSprite.js';
import { CompositeAvatar, DEFAULT_APPEARANCE, preloadPlayerAppearance, loadAppearanceIfNeeded } from '../sprites/CompositeAvatar.js';
import { AnimatedDoor } from '../sprites/AnimatedDoor.js';
import { ZoneDimmer, type LitArea } from '../systems/ZoneDimmer.js';
import { AmbientLight } from '../systems/AmbientLight.js';
import { PersonalDesks, type DeskArea } from '../systems/PersonalDesks.js';
import { Scooters, type ScooterCandidate } from '../systems/Scooters.js';
import { PortalPresence, type PresencePerson } from '../systems/PortalPresence.js';
import { Elevators, type ElevatorCabin } from '../systems/Elevators.js';
import type { ElevatorDoor } from '../sprites/ElevatorDoor.js';
import { findPath, nearestFreeCell, cellOf, isBlocked, type WalkGrid, type Point } from '../systems/Pathfinder.js';
import { addExtrudedTilesetImage } from '../loaders/extrudeTiles.js';
import { ReactionEffects } from '../systems/Reactions.js';
import { saveLastPlace } from '../../lib/lastPlace.js';
import { getTenantSlug } from '../../lib/tenant.js';
import { API_BASE, assetUrl } from '../../lib/apiBase.js';
import { ZOOM_LEVELS, ZOOM_MIN, ZOOM_MAX, nextZoomLevel, attachWheelZoom } from '../../lib/zoom.js';
import { canEnterZoneHere, isZoneClosedHere, sessionAccess } from '../../lib/access.js';


/**
 * ZoneScene — Scene Phaser principale pour une zone du campus.
 *
 * REGLES :
 * - Ne jamais importer de composants React ici.
 * - Ne jamais ecrire dans Zustand directement — passer par EventBus.
 * - Les positions x/y des joueurs ne sortent JAMAIS de cette scene vers React.
 * - Calculer les positions screen ici et les emettre via EventBus une fois par frame.
 */

const PLAYER_SPEED = 280; // px/s
/** Corps physique de l'avatar (px) : ses pieds, `lift` px au-dessous de l'ancre pour le bas. */
const PLAYER_BODY = { w: 16, h: 10, lift: 1 };
/** Glissement dans les coins : on bute sur un obstacle qui ne mord que de ce nombre de px sur le corps → on glisse à côté. */
const CORNER_SLIDE_PX = 10;
const SPRINT_MULTIPLIER = 1.6; // vitesse en sprint (Shift maintenu)
/**
 * Touches de déplacement, par position physique (`KeyboardEvent.code`) :
 * WASD en QWERTY = ZQSD en AZERTY, plus les flèches.
 */
const MOVE_CODES = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight']);

/**
 * Champ où l'on tape du texte, ou tableau blanc ouvert en demi-écran (ses
 * raccourcis sont des lettres) : ses touches ne déplacent pas l'avatar.
 */
function isTextField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.closest('.pl-whiteboard')) return true;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable;
}

// ── Depth sorting constants ─────────────────────────────────────────────────
// Formule avatar : depth = DEPTH_Y_BASE + avatar.y / DEPTH_Y_SCALE
// Formule tile abovePlayer : depth = DEPTH_Y_BASE + (pixelY + tileH) / DEPTH_Y_SCALE
// Pour une map max ~200 tiles × 32 = 6400px, depth avatar max = 100 + 640 = 740.
const DEPTH_Y_BASE     = 100;
const DEPTH_Y_SCALE    = 10;
/** Portes de l'ascenseur : 7 images à 10/s pour s'ouvrir ou se fermer. */
const ELEVATOR_DOORS_MS = 700;
/** Portes jointes : la dernière image de la fermeture s'affiche à 600 ms. */
const ELEVATOR_DOORS_CLOSED_MS = 600;
/** Pas de l'avatar entre le palier et la cabine. */
const ELEVATOR_STEP_MS = 400;
/** Sans réponse du serveur à la demande de place (pas connecté) : place 0. */
const ELEVATOR_REPLY_TIMEOUT_MS = 600;
/** Arrivée : attente des avatars déjà là (room:state) avant d'ouvrir, au plus. */
const ELEVATOR_ARRIVAL_WAIT_MS = 3_000;
/** Avatar d'un autre entré dans la cabine qui ne part pas : rendu au serveur après. */
const ELEVATOR_REMOTE_SCRIPT_MS = 4_000;
/**
 * Invite « S'asseoir / Se lever » à droite de l'avatar : point visé (px du
 * monde) = bord droit de l'avatar à mi-hauteur ; ScooterPrompt y pose le bord
 * gauche de l'invite, avec un écart en pixels d'écran (au-dessus de la tête,
 * elle se mêlait au nom ; centrée sur un point fixe, elle retombait sur
 * l'avatar en dézoomant).
 */
const SEAT_PROMPT_SIDE_X = 18;
const SEAT_PROMPT_SIDE_Y = 22;
/** En se levant, on recule d'une case dans le dos du siège. */
const STAND_BACK: Record<SeatDirection, [number, number]> = { up: [0, 1], down: [0, -1], left: [1, 0], right: [-1, 0] };
/** Invite « Ouvrir le tableau » à cette distance du tableau (px du monde). */
const BOARD_PROMPT_DISTANCE = 56;

/** Place repérée sur la carte : ancre de l'avatar assis, direction, relèvement de l'image (`seatSpots`). */
type SeatSpot = { x: number; y: number; direction: SeatDirection; lift: number; sprite: Phaser.GameObjects.Image };
// Layers Tiled normaux : calques de sol (nom en "floor…") sous les sols peints
// dans l'éditeur, les autres (mobilier en tuiles, bas de murs) au-dessus —
// voir FLOOR_DEPTHS (FloorRenderer.ts).
const DEPTH_PROXIMITY  = 50;      // cercle de bulle — sous les avatars
const DEPTH_SPOTLIGHT  = 40;      // spotlight podium
const DEPTH_MOVE_TARGET = 60;     // marqueur de destination tap-to-move
const DEPTH_ALWAYS_ABOVE = 800;   // layers alwaysAbove (murs) — au-dessus des avatars
const DEPTH_DESK_LABEL = 900;     // labels bureaux — au-dessus de tout
const DEPTH_REACTION   = 950;     // emoji réactions
const DEPTH_SPEAKER    = 910;     // speaker ping ring
/** Fondu au noir au passage d'un portail, puis à l'arrivée (fondu-porte du design system). */
const PORTAL_FADE_MS = 250;
/** Posé par le fondu de sortie d'un portail : la carte suivante s'ouvre en fondu. */
let fadeInOnArrival = false;
/**
 * « Aller à mon bureau » et « Rejoindre » une personne : trajet à pied, de
 * carte en carte (sortir de la salle, prendre l'escalier, sortir du
 * bâtiment…), jamais posé directement à destination. Gardé entre les cartes
 * (la scène redémarre à chaque carte), abandonné dès qu'une touche de
 * déplacement reprend la main. Avec `userId`, la destination est l'avatar de
 * cette personne, suivi s'il bouge (`followPerson`).
 */
let walkRoute: {
  mapId: string; x: number; y: number; graph: Array<{ from: string; to: string }>;
  userId?: string; name?: string;
} | null = null;
/** Après une arrivée, l'itinéraire est recalculé quand murs et mobilier arrivent (obstacles), pendant ce délai. */
const WALK_ROUTE_REPLAN_MS = 3000;
/** Fin de marche à cette distance d'un portail du trajet : on le franchit (sa case peut être bloquée). */
const WALK_ROUTE_PORTAL_REACH = 48;
/** « Rejoindre » : on s'arrête à cette distance de la personne (une case à côté). */
const PERSON_REACH = 48;
/** « Rejoindre » : l'itinéraire est recalculé quand la personne s'est éloignée de plus que ça du point visé… */
const PERSON_REPLAN_DISTANCE = 32;
/** … au plus une fois par intervalle. */
const PERSON_REPLAN_MS = 500;
/** « Rejoindre » : délai pour trouver la personne en arrivant sur sa carte (avatars reçus après l'arrivée). */
const PERSON_SEARCH_MS = 5000;
/**
 * Zoom choisi par la personne avant qu'une petite carte ne l'agrandisse
 * d'office (`minZoomForMap`), et le zoom imposé (gardés aussi après un
 * rechargement de la page) : en arrivant sur la carte
 * suivante, on repart du choix de la personne si elle n'a pas zoomé depuis.
 */
const ZOOM_FIT_KEY = 'campus:zoomBeforeFit';
function readZoomBeforeFit(): { pref: number; fitted: number } | null {
  try { return JSON.parse(localStorage.getItem(ZOOM_FIT_KEY) ?? 'null') as { pref: number; fitted: number } | null; } catch { return null; }
}
function writeZoomBeforeFit(value: { pref: number; fitted: number } | null): void {
  try {
    if (value) localStorage.setItem(ZOOM_FIT_KEY, JSON.stringify(value));
    else localStorage.removeItem(ZOOM_FIT_KEY);
  } catch { /* stockage indisponible : on garde le zoom tel quel */ }
}

// Halo d'approche sur la porte à portée d'interaction (postFX, WebGL uniquement).
const DOOR_GLOW_COLOR          = 0x4f6ef7; // --color-primary (Nocturne)
const DOOR_GLOW_OUTER_STRENGTH = 4;
const DOOR_GLOW_INNER_STRENGTH = 0;

function samePositions(
  a: Map<string, { x: number; y: number }>,
  b: Map<string, { x: number; y: number }>,
): boolean {
  if (a.size !== b.size) return false;
  for (const [id, pos] of b) {
    const prev = a.get(id);
    if (!prev || prev.x !== pos.x || prev.y !== pos.y) return false;
  }
  return true;
}

/** Mapping nom de fichier TMX (sans extension) → slug de zone dans notre codebase */
const TMX_TO_SLUG: Record<string, string> = {
  'copyhouse_conference': 'meetingroom',
  'copyhouse_main':       'lobby',
};

// ── Types pour les objets TMJ du layer "zones" ──────────────────────────────

interface DoorCatalogEntry {
  id: string;
  url: string;
  frameWidth: number;
  frameHeight: number;
  frames: number;
  hasLocked: boolean;
  lockedId: string | null;
}

interface TmjDoor {
  id: string;
  name: string;
  bounds: { x: number; y: number; width: number; height: number };
  linkedBubble: string;
  /**
   * Fix I-2 (Task 19) : id de bulle résolu — comparable à player.bubbleId
   * (broadcast serveur, UUID map_zones.id pour les bulles créées via
   * l'éditeur, nom TMJ pour les bulles legacy). `linkedBubble` reste le nom
   * en clair (affichage, ex. DoorPrompt) — ne jamais l'utiliser pour une
   * comparaison de bubbleId, toujours resolvedBubbleId.
   * Initialisé = linkedBubble (identité, correct pour les bulles TMJ), puis
   * réécrit par loadZonesFromDB() une fois la table nom→id construite.
   */
  resolvedBubbleId: string;
  doorSprite: string | null;
}

interface TmjArea {
  name: string;
  bounds: { x: number; y: number; width: number; height: number };
  url: string;
  action: string;
  label: string;
}

interface TmjDesk {
  id: string;
  deskId: string;
  name: string;
  bounds: { x: number; y: number; width: number; height: number };
  label: string;
}

interface TmjPodium {
  name: string;
  bounds: { x: number; y: number; width: number; height: number };
}

type AlphaMap = { w: number; h: number; at: (x: number, y: number) => number };
const furnitureAlphaCache = new Map<string, AlphaMap | null>();

/**
 * Emprise au sol d'un objet dans une bande de cases (x de `x0` à `x1`, rangée
 * qui commence en `top`, `ts` px de haut) : largeur des pixels pleins
 * (alpha ≥ 200, l'ombre ne compte pas) dans les 16 px du bas de la bande,
 * hauteur du dernier pixel plein jusqu'à 24 px au-dessus. Null si la bande est vide.
 */
function footprintInBand(alpha: AlphaMap, x0: number, x1: number, top: number, ts: number): { x0: number; x1: number; y0: number; y1: number } | null {
  let minX = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let y = Math.max(0, top); y < Math.min(alpha.h, top + ts); y++) {
    for (let x = Math.max(0, x0); x < Math.min(alpha.w, x1); x++) {
      if (alpha.at(x, y) < 200) continue;
      maxY = Math.max(maxY, y);
      if (y >= top + ts - 16) { minX = Math.min(minX, x); maxX = Math.max(maxX, x); }
    }
  }
  if (maxY < 0 || minX > maxX) return null;
  const pad = Math.max(0, 6 - (maxX - minX + 1)) / 2;   // 6 px de large au moins
  const y1 = maxY + 1 - top;
  return { x0: minX - x0 - pad, x1: maxX + 1 - x0 + pad, y0: Math.max(0, y1 - 24), y1 };
}

export class ZoneScene extends Phaser.Scene {
  // Containers des joueurs DISTANTS uniquement, cle = sessionId Colyseus.
  private remotePlayerSprites = new Map<string, CompositeAvatar>();
  // Reverse-lookup sessionId → userId pour emitOverlayPositions.
  private sessionIdToUserId = new Map<string, string>();
  private localPlayerId: string | null = null;
  private zoneConfig: ZoneConfig | null = null;
  private zoneUrls: ZoneAssetUrls | null = null;
  private zoneLoader!: ZoneLoader;

  private localPlayer: CompositeAvatar | null = null;
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;
  /**
   * Touches de déplacement tenues (MOVE_CODES), par position physique
   * (`KeyboardEvent.code`) et non par lettre : KeyW/KeyA/KeyS/KeyD = ZQSD sur
   * un clavier AZERTY, WASD sur un QWERTY, sans rien détecter. Flèches
   * comprises : elles passaient par les touches Phaser, qui pouvaient ne pas
   * répondre à l'arrivée (retours Copyhouse, 2026-10-29).
   */
  private heldMoveCodes = new Set<string>();
  private keySpace: Phaser.Input.Keyboard.Key | null = null;
  private keyE: Phaser.Input.Keyboard.Key | null = null;
  private lastDirection: 'up' | 'down' | 'left' | 'right' = 'down';
  private mapLoadFailed = false;

  // Spawn points nommes extraits du TMJ (type="spawn" ou type="start")
  private spawnPoints = new Map<string, { x: number; y: number }>();
  // Portal id → spawn name
  private portalSpawnNames = new Map<string, string>();

  // Layers avec collisions (Task 1)
  private collisionLayers: Phaser.Tilemaps.TilemapLayer[] = [];
  // Collision bodies for admin-placed solid furniture (ME-4d)
  private furnitureBodies = new Map<string, Phaser.Physics.Arcade.Image[]>();
  // Mobilier de l'éditeur affiché (retiré puis reposé à chaque rechargement en direct).
  private dbFurniture: Array<{ id: string; item: { furnitureId: string; tileX: number; tileY: number; layer: string }; sprite: Phaser.GameObjects.Image; bodies: Phaser.Physics.Arcade.Image[] }> = [];
  private furnitureGeneration = 0;
  // Murs posés via l'éditeur (map_walls) — rendu pixel-art (WallRenderer) +
  // collision synthétique, même principe que furnitureBodies/doorCollisionBodies.
  private wallRenderer: WallRenderer | null = null;
  // Sols peints via l'éditeur (map_floor_layers). Visuels, sauf l'eau
  // (étang, eau profonde, piscine, sol « Eau ») : collision, voir
  // rebuildWaterBodies().
  private floorRenderer: FloorRenderer | null = null;
  private floorReloadTimer: ReturnType<typeof setTimeout> | null = null;
  private floorData = new Map<FloorLayerName, FloorLayerData>();
  // Cases couvertes par un objet posé « au sol » (pont, ponton, tapis) :
  // praticables même sur l'eau. Clé = y * MAX_FLOOR_DIMENSION + x.
  private floorObjectCells = new Set<number>();
  private waterBodies: Phaser.Physics.Arcade.Image[] = [];
  // Étiquettes de texte posées via l'éditeur (map_labels).
  private labelRenderer: LabelRenderer | null = null;
  private wallBodies = new Map<string, Phaser.Physics.Arcade.Image>();
  // Reference tilemap pour manipulation des tuiles (portes)
  private tilemap: Phaser.Tilemaps.Tilemap | null = null;

  // Portes du TMJ (Task 5)
  private doorZones: TmjDoor[] = [];
  private nearbyDoorId: string | null = null;
  private readonly DOOR_INTERACT_DISTANCE = 64; // px — distance du bord du rectangle de la porte
  private doorSprites = new Map<string, AnimatedDoor>();
  /** Porte qui porte actuellement le halo d'approche (voir updateDoorGlow). */
  private glowedDoor: { sprite: AnimatedDoor; fx: Phaser.FX.Glow } | null = null;
  private nearbyDoorLocked = false;
  private lastDoorPromptPos: { x: number; y: number } | null = null;
  /** Voile hors zone active (bulle / salle de réunion / bureau) — voir ZoneDimmer. */
  private zoneDimmer: ZoneDimmer | null = null;
  /** Lumière d'ambiance (réglage « Lumière d'ambiance », touche L) — voir AmbientLight. */
  private ambientLight: AmbientLight | null = null;
  /** bubbleId → rectangle (px), source GET /world/zones/:slug/bubble-zones. */
  private bubbleBounds = new Map<string, LitArea>();
  /**
   * Désabonnements (EventBus, store, timers) de la scène courante, exécutés à
   * SHUTDOWN (changement de carte : BootScene → ZoneScene redémarre la scène)
   * et à DESTROY. Sans ça, chaque changement de carte empilait une copie de
   * chaque handler EventBus (tous exécutés N fois après N changements).
   */
  private sceneCleanups: Array<() => void> = [];
  // Corps de collision synthétique pour les portes créées via l'éditeur
  // (map_zones) — pas de tuile "collides" peintée à l'avance possible pour
  // ces portes, donc un corps Arcade Physics dédié (même principe que le
  // mobilier solide, furnitureBodies) plutôt qu'un toggle de tuile. Les
  // portes du TMJ gardent leur mécanisme tuile existant, inchangé.
  private doorCollisionBodies = new Map<string, Phaser.Physics.Arcade.Image>();
  /**
   * Bulle sans porte (salon ouvert) fermée par ses occupants : mur invisible
   * pour qui n'y est pas autorisé, « Toquer » à son bord (porte fictive
   * `lock:{bubbleId}` dans checkDoors / triggerDoorAction).
   */
  private openLockBarriers = new Map<string, Phaser.Physics.Arcade.Image>();

  // Zones area (Sprint 5 — iframe overlay)
  private areaZones: TmjArea[] = [];
  private insideAreaName: string | null = null;

  // Desk zones (Sprint 11a)
  private deskZones: TmjDesk[] = [];
  private insideDeskId: string | null = null;
  private deskLabels = new Map<string, Phaser.GameObjects.Text>();
  // Bureaux personnels (espaces bureaux + bureaux posés par les membres).
  private personalDesks: PersonalDesks | null = null;
  private elevators: Elevators | null = null;
  /** Avatar qui entre dans l'ascenseur ou en sort : pas de commandes, corps coupé. */
  private elevatorWalking = false;
  /** Profondeur imposée à l'avatar local (devant ou derrière les portes de l'ascenseur). */
  private localDepthOverride: number | null = null;
  /** Étage choisi, en attente d'une place dans la cabine (elevator:slot / elevator:full). */
  private pendingElevatorBoard: { zoneId: string; direction: 'up' | 'down'; then: () => void; timer: Phaser.Time.TimerEvent | null; warned: boolean } | null = null;
  /** Arrivé dans la cabine, portes fermées, en attente des avatars déjà sur le palier. */
  private pendingElevatorArrival: { zoneId: string; origin: { x: number; y: number }; timer: Phaser.Time.TimerEvent } | null = null;
  /** Avatars des autres que l'ascenseur fait entrer ou sortir : positions du serveur mises de côté. */
  private remoteElevatorScripts = new Map<string, { latest: PlayerStateDTO | null; end: Phaser.Time.TimerEvent }>();
  /** Profondeur imposée à un avatar distant (devant ou derrière les portes). */
  private remoteDepthOverrides = new Map<string, number>();
  // ── Trottinettes (game/systems/Scooters.ts) ──
  private scooters: Scooters | null = null;
  private portalPresence: PortalPresence | null = null;
  /** État reçu de la room avant la création de `scooters`. */
  private pendingScooterState: ScooterSnapshot[] | null = null;
  /** Modèle sur lequel roule le joueur local (confirmé par le serveur), null = à pied. */
  private localVehicle: ScooterModel | null = null;
  private nearbyScooter: ScooterCandidate | null = null;
  private scooterPromptMode: 'mount' | 'dismount' | 'sit' | 'stand' | 'board' | 'notice' | null = null;
  private lastScooterPromptPos: { x: number; y: number } | null = null;

  // Assise (@campus/core/seats) : sièges des meubles affichés (par id de meuble),
  // siège du joueur local (confirmé par le serveur) et sièges occupés par les
  // autres (par sessionId).
  private seats = new Map<string, SeatSpot>();
  /** Tableaux blancs posés (utilisables) : itemId → point de l'invite (haut de l'objet, centre) et nom. */
  /** Tableaux blancs à portée, par id d'objet affiché ; `boardId` = id connu de l'API (sans le préfixe `pd-item-` des objets de bureau). */
  /** Tableaux blancs (`whiteboard`) et panneaux d'affichage des duos (`notice`) à portée de main. */
  private boards = new Map<string, { x: number; y: number; footY: number; name: string; boardId: string; kind: 'whiteboard' | 'notice' }>();
  private nearbyBoard: string | null = null;
  private localSeat: Seat | null = null;
  private localSeatValue = '';
  private nearbySeat: SeatSpot | null = null;
  private remoteSeats = new Map<string, string>();
  /** Écart ancre de l'avatar − centre de son corps physique, relevé debout (sert à replacer l'avatar en se levant). */
  private bodyAnchorOffset = { x: 0, y: PLAYER_BODY.h / 2 - PLAYER_BODY.lift };

  // Podium zones (Sprint 11d)
  private podiumZones: TmjPodium[] = [];
  private insidePodiumName: string | null = null;
  private podiumSpotlight: Phaser.GameObjects.Graphics | null = null;

  // Throttle des mises a jour Colyseus (~20 FPS)
  private lastMoveEmitTime = 0;
  private lastEmittedAnimation: 'idle' | 'walk' = 'idle';
  private readonly MOVE_EMIT_INTERVAL_MS = 50;

  // Limites du monde en pixels
  private mapWidth = 0;
  private mapHeight = 0;

  // ── Wave double-clic ────────────────────────────────────────
  private lastClickTime = 0;
  /** Premier clic sur la carte (double-clic = y aller), en coordonnées écran. */
  private lastMapClick: { time: number; x: number; y: number } | null = null;
  private lastClickSessionId: string | null = null;
  private readonly DOUBLE_CLICK_MS = 350;
  /** Curseur « main » affiché au survol d'un avatar distant. */
  private hoveringAvatar = false;

  // Anti portal ping-pong
  private transitionCooldown = false;
  /** Portail « Réservé » où se tient l'avatar (toast une seule fois par passage). */
  private reservedPortalId: string | null = null;
  /** Fondu de sortie d'un portail en cours : l'avatar ne bouge plus. */
  private portalFading = false;
  private readonly TRANSITION_DEBOUNCE_MS = 1500;
  private readonly HYSTERESIS_BUFFER_PX = 32;

  // Suspend mouvement pendant la saisie chat React
  private chatInputActive = false;

  // Tap-to-move tactile — destination monde ; le clavier reprend la main dès qu'une touche est pressée
  private moveTarget: { x: number; y: number } | null = null;
  private moveTargetMarker: Phaser.GameObjects.Graphics | null = null;
  private readonly ARRIVE_THRESHOLD_PX = 4;
  /** Étapes restantes de l'itinéraire (moveTarget = la première). */
  private movePath: Point[] = [];
  /** Destination finale demandée (pour recalculer l'itinéraire si l'avatar bloque). */
  private moveFinalTarget: Point | null = null;
  /** Portes non verrouillées sur l'itinéraire, ouvertes automatiquement à l'approche. */
  private movePathDoorIds = new Set<string>();
  private moveStuckSince = 0;
  private moveLastPos: Point | null = null;
  private moveReplanned = false;
  /** Portail visé par l'étape en cours du trajet vers le bureau (walkRoute). */
  private walkRoutePortal: Portal | null = null;
  private walkRouteReplanUntil = 0;
  /** « Rejoindre » : point visé, prochain recalcul permis, fin de la recherche sur la carte de la personne. */
  private personAim: { x: number; y: number } | null = null;
  private personReplanAt = 0;
  private personSearchUntil = 0;

  // Handler bound — retiré à la fermeture de la scène (voir listen())
  private readonly onRestartScene = () => {
    this.scene.start('BootScene');
  };

  // Dernières positions émises par emitOverlayPositions() — une Map identique
  // (rien n'a bougé à l'écran) n'est pas réémise.
  private lastOverlayPositions: Map<string, { x: number; y: number }> | null = null;

  // ── Bulle de proximite visuelle ───────────────────────────
  private proximityGraphics: Phaser.GameObjects.Graphics | null = null;
  private localBubbleActive = false;
  private proximityNearbyCount = 0;

  // ── Sprint (Shift maintenu) ───────────────────────────────
  private keyShift: Phaser.Input.Keyboard.Key | null = null;

  // ── Pan caméra manuel (clic maintenu + glisser, explorer la carte) ──
  private readonly PAN_DRAG_THRESHOLD_PX = 6;
  private isPanningCamera = false;
  private cameraDetached = false;
  /**
   * Salle de réunion cadrée : la caméra suit ce point invisible au centre de
   * la salle au lieu de l'avatar, pour que toute la salle tienne entre la
   * rangée de vignettes et la barre du bas (updateRoomFraming).
   */
  private roomCameraTarget: Phaser.GameObjects.Zone | null = null;
  /** Cible suivie par la caméra (avatar ou salle) : le suivi n'est relancé qu'au changement. */
  private cameraFollowing: Phaser.GameObjects.GameObject | null = null;
  private panPointerDownX = 0;
  private panPointerDownY = 0;
  private panStartScrollX = 0;
  private panStartScrollY = 0;

  // ── Réactions emoji ───────────────────────────────────────
  private reactions: ReactionEffects | null = null;

  // ── Indicateur parole (speaker ping) ──────────────────────
  private speakerPings = new Map<string, Phaser.GameObjects.Graphics>();

  // ── Verrouillage bulles côté client ──────────────────────────
  private lockedBubbleIds = new Set<string>();
  private allowedBubbleIds = new Set<string>();

  // Fix I-2 (Task 19) : Task 13 a changé l'id de bulle circulant (broadcast
  // serveur, player.bubbleId) de z.name vers map_zones.id pour les zones
  // créées via l'éditeur. `linkedBubble` sur une porte reste un NOM en clair
  // (saisi par un admin dans EditorPage.tsx) — table de correspondance
  // nom→id (bulles DB uniquement) construite à chaque loadZonesFromDB(),
  // utilisée pour résoudre TmjDoor.resolvedBubbleId une fois au chargement.
  // Les bulles TMJ ne sont pas dans cette map (id === name, pas de résolution
  // nécessaire) — le fallback `?? linkedBubble` les couvre.
  private bubbleNameToId = new Map<string, string>();
  /** Sens inverse de bubbleNameToId — poussée vers gameStore.bubbleNames
   *  (setBubbleNames) pour que le HUD (BubleIndicator) affiche le nom réel
   *  d'une bulle DB au lieu de son id UUID (bug trouvé 2026-08-23). */
  private bubbleIdToName = new Map<string, string>();

  constructor() {
    super({ key: 'ZoneScene' });
  }

  // ── Lifecycle ──────────────────────────────────────────────

  init(data: { zoneConfig: ZoneConfig; localPlayerId: string; urls: ZoneAssetUrls }) {
    this.zoneConfig    = data.zoneConfig;
    this.localPlayerId = data.localPlayerId;
    this.zoneUrls      = data.urls;

    // Reset entre zones
    this.transitionCooldown   = false;
    this.reservedPortalId     = null;
    this.elevators            = null;
    this.elevatorWalking      = false;
    this.localDepthOverride   = null;
    this.pendingElevatorBoard = null;
    this.pendingElevatorArrival = null;
    this.remoteElevatorScripts = new Map();
    this.remoteDepthOverrides = new Map();
    this.portalFading         = false;
    this.walkRoutePortal      = null;
    this.walkRouteReplanUntil = 0;
    this.personAim            = null;
    this.personReplanAt       = 0;
    this.personSearchUntil    = 0;
    this.chatInputActive      = false;
    this.localPlayer          = null;
    this.roomCameraTarget     = null;
    this.cameraFollowing      = null;
    this.proximityGraphics    = null;
    this.moveTarget           = null;
    this.moveTargetMarker     = null;
    this.movePath             = [];
    this.moveFinalTarget      = null;
    this.movePathDoorIds      = new Set();
    this.localBubbleActive    = false;
    this.proximityNearbyCount = 0;
    this.lastDirection        = 'down';
    this.lastMoveEmitTime     = 0;
    this.lastEmittedAnimation = 'idle';
    this.mapWidth             = 0;
    this.mapHeight            = 0;
    this.remotePlayerSprites.clear();
    this.sessionIdToUserId.clear();
    this.mapLoadFailed = false;
    this.spawnPoints.clear();
    this.portalSpawnNames.clear();
    this.collisionLayers = [];
    this.tilemap = null;
    this.doorZones = [];
    this.bubbleNameToId.clear();
    this.nearbyDoorId = null;
    this.doorSprites.clear();
    // Sans ça, revenir sur une carte déjà visitée gardait les corps (détruits
    // avec l'ancienne scène) de ses portes : ensureDoorCollisionBodies les
    // croyait déjà créés et les portes fermées se traversaient (2026-10-29).
    this.doorCollisionBodies.clear();
    this.openLockBarriers = new Map();
    this.speakerPings.clear();
    this.glowedDoor = null;
    this.lastOverlayPositions = null;
    this.zoneDimmer = null;
    this.bubbleBounds.clear();
    useRoomClaimsStore.getState().setClaims([]);
    useRoomClaimsStore.getState().setClaimable([]);
    this.nearbyDoorLocked = false;
    this.lastDoorPromptPos = null;
    this.scooters = null;
    this.pendingScooterState = null;
    this.localVehicle = null;
    this.nearbyScooter = null;
    this.scooterPromptMode = null;
    this.reactions = null;
    this.lastScooterPromptPos = null;
    this.seats = new Map();
    this.boards = new Map();
    this.nearbyBoard = null;
    this.localSeat = null;
    this.localSeatValue = '';
    this.nearbySeat = null;
    this.remoteSeats = new Map();
    this.areaZones = [];
    this.insideAreaName = null;
    this.deskZones = [];
    this.dbFurniture = [];
    this.furnitureBodies.clear();
    this.insideDeskId = null;
    this.deskLabels.clear();
    this.podiumZones = [];
    this.insidePodiumName = null;
    this.podiumSpotlight = null;
    this.keySpace = null;
    this.keyE = null;
    // Leurs objets Phaser appartiennent à la carte précédente, détruite au
    // redémarrage de la scène : sans ça, sol, murs et textes de la nouvelle
    // carte ne s'affichent pas après un changement de carte.
    this.floorRenderer = null;
    this.floorData.clear();
    this.wallRenderer = null;
    this.labelRenderer = null;
  }

  preload() {
    if (!this.zoneConfig || !this.zoneUrls) return;

    // Sans crossOrigin='anonymous', les textures chargées cross-origin (tilesets
    // LimeZu depuis api.lumetic.fr) sont "tainted" pour WebGL — rendu noir silencieux,
    // même si le serveur renvoie les bons headers CORS (l'<img> ne les demande jamais).
    this.load.crossOrigin = 'anonymous';

    this.zoneLoader = new ZoneLoader(this);
    this.zoneLoader.loadZone(this.zoneConfig, this.zoneUrls, useGameStore.getState().jwt ?? '');
    // Pas de preloadFurniture() ici (retiré le 2026-09-26) : il chargeait
    // TOUT le catalogue (1 013 images, ~4 Mo) pour chaque joueur à chaque
    // carte. loadFurnitureFromDB() charge à la demande les seules images des
    // meubles réellement posés — le coût ne dépend plus de la taille du
    // catalogue. Le préchargement de la palette reste dans EditorScene.
    preloadWallAtlas(this);
    preloadFloorAtlas(this);

    // Preload door spritesheets from catalog
    const catalog = doorsCatalog as DoorCatalogEntry[];
    for (const door of catalog) {
      if (!this.textures.exists(door.id)) {
        this.load.spritesheet(door.id, door.url, {
          frameWidth: door.frameWidth,
          frameHeight: door.frameHeight,
        });
      }
    }

    // Lazy load : uniquement les calques du joueur local (5-6 textures, pas 432)
    const localAppearance = useGameStore.getState().appearance ?? DEFAULT_APPEARANCE;
    preloadPlayerAppearance(this, localAppearance);

    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      console.warn(`[ZoneScene] Asset 404 — cle="${file.key}" url="${file.src as string}" — fallback map active`);
      if (file.key.startsWith('map:')) {
        this.mapLoadFailed = true;
      }
    });
  }

  async create() {
    if (import.meta.env.DEV) {
      (window as any).__PHASER_GAME__ = this.sys.game;
      (window as any).__ZONE_SCENE__ = this;
    }

    if (!this.zoneConfig || !this.zoneUrls) return;

    // Un seul enregistrement par événement même si create() est rejoué.
    this.events.off(Phaser.Scenes.Events.SHUTDOWN, this.runSceneCleanups, this);
    this.events.off(Phaser.Scenes.Events.DESTROY, this.runSceneCleanups, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.runSceneCleanups, this);
    this.events.once(Phaser.Scenes.Events.DESTROY, this.runSceneCleanups, this);

    // Capturer la position du joueur avant destruction (ex: aller-retour
    // panneau admin, qui démonte GamePage → game.destroy(true) → ferme la
    // connexion Colyseus) pour la restaurer au prochain create() sur la même
    // zone, plutôt que de retomber systématiquement sur le spawn de la carte.
    // "destroy" seul est émis par game.destroy(true) — jamais "shutdown"
    // (voir commit 8753c25, même piège vécu sur EditorScene).
    const zoneSlugForPosition = this.zoneConfig.slug;
    this.events.once(Phaser.Scenes.Events.DESTROY, () => {
      if (this.localPlayer) {
        useGameStore.getState().setLastPlayerPosition({
          zoneSlug: zoneSlugForPosition,
          x: this.localPlayer.x,
          y: this.localPlayer.y,
        });
      }
    });

    const zoneId = this.zoneConfig.id;

    // ── 1. Tilemap ────────────────────────────────────────────

    if (this.mapLoadFailed || !this.cache.tilemap.exists(`map:${zoneId}`)) {
      console.warn(`[ZoneScene] Map introuvable pour "${zoneId}" — fallback 800x600`);
      this.mapWidth  = 800;
      this.mapHeight = 600;
      this.add.rectangle(400, 300, 800, 600, 0x1a1a2e);
      this.finishCreate();
      return;
    }

    const map = this.make.tilemap({ key: `map:${zoneId}` });
    this.tilemap = map;

    const tilesetObjects: Phaser.Tilemaps.Tileset[] = [];
    for (let i = 0; i < map.tilesets.length; i++) {
      const ts = map.tilesets[i];
      if (!ts) continue;
      const added = addExtrudedTilesetImage(this, map, ts, `tileset:${zoneId}:${i}`);
      if (added) tilesetObjects.push(added);
    }

    // ── 2. Layers — collisions + depth sorting ────────────────

    for (const layerData of map.layers) {
      const layer = map.createLayer(layerData.name, tilesetObjects);
      if (!layer) continue; // objectgroups retournent null

      // Task 1 : collision sur toutes les tuiles avec propriete collides: true
      layer.setCollisionByProperty({ collides: true });
      this.collisionLayers.push(layer);

      // Depth sorting : les layers avec la propriete Tiled "abovePlayer: true"
      // ont un depth par tile basé sur leur Y, permettant aux avatars de passer
      // devant ou derrière les meubles selon leur position.
      // Formule : depth = DEPTH_Y_BASE + (pixelY + tileHeight) / DEPTH_Y_SCALE
      const isAbovePlayer = getTiledProperty(
        (layerData as unknown as { properties?: unknown }).properties,
        'abovePlayer',
      );
      // alwaysAbove : murs — depth fixe élevé, couvre toujours l'avatar
      const isAlwaysAbove = getTiledProperty(
        (layerData as unknown as { properties?: unknown }).properties,
        'alwaysAbove',
      );

      if (isAlwaysAbove) {
        layer.setDepth(DEPTH_ALWAYS_ABOVE);
      } else if (isAbovePlayer) {
        // abovePlayer : mobilier — tri Y par tile, l'avatar passe devant ou derrière
        layer.setVisible(false);
        layer.forEachTile((tile) => {
          if (tile.index === -1) return;
          const tileset = map.tilesets.find((ts) => ts.firstgid <= tile.index && tile.index < ts.firstgid + ts.total);
          if (!tileset) return;

          const tsKey = `tileset:${zoneId}:${map.tilesets.indexOf(tileset)}`;
          const frame = tile.index - tileset.firstgid;

          const sprite = this.add.sprite(tile.pixelX, tile.pixelY, tsKey, frame);
          sprite.setOrigin(0, 0);
          sprite.setDepth(DEPTH_Y_BASE + (tile.pixelY + map.tileHeight) / DEPTH_Y_SCALE);
        });
      } else {
        // Layers normaux : depth faible, toujours sous les avatars — les
        // calques de sol du TMJ passent sous les sols peints dans l'éditeur.
        layer.setDepth(/^floor/i.test(layerData.name) ? FLOOR_DEPTHS.tmjFloor : FLOOR_DEPTHS.tmjOther);
      }
    }

    this.mapWidth  = map.widthInPixels;
    this.mapHeight = map.heightInPixels;

    // ── 2b. Objets du layer "zones" (spawns, portails, portes) ─
    this.parseZoneObjects(map);

    // ── 2c. Sprites de portes animées (portes du TMJ) ─────────
    this.spawnDoorSprites(this.doorZones);

    console.log('[ZoneScene] apres parseZoneObjects — spawns:', this.spawnPoints.size,
      'portals:', this.zoneConfig?.portals.length,
      'doors:', this.doorZones.length, this.doorZones.map(d => d.name));

    // ── Zones créées via l'éditeur (map_zones) — bulle/porte/bureau/portail/
    // spawn. Le TMJ ne contient plus que les spawns sur les cartes migrées
    // (voir docs/TASKS.md). Rejoué en live sur 'colyseus:zones-changed'
    // (relais du canal Redis zones:{tenantId}:{mapId}, voir ZoneRoom côté
    // serveur) — pas besoin de recharger la page après une édition.
    //
    // Attendu avant finishCreate() : un spawn map_zones doit pouvoir
    // remplacer/compléter un spawn TMJ dans la résolution qui suit — sinon
    // le joueur spawnerait sur l'ancienne position, puis enverrait CETTE
    // position au serveur à la connexion Colyseus (zone:entered → joinOrCreate),
    // qui la renverrait ensuite tel quel via room:state → syncLocalPlayerSpawn(),
    // écrasant après coup toute correction locale tardive (course perdue
    // d'avance, vécue en test le 2026-08-11).
    this.createPersonalDesks();
    this.scooters = new Scooters(this, this.TILE_SIZE);
    if (this.pendingScooterState) this.scooters.setState(this.pendingScooterState);
    this.pendingScooterState = null;
    this.sceneCleanups.push(() => {
      this.scooters?.destroy();
      if (this.scooterPromptMode) EventBus.emit('scooter:prompt-hidden', {});
    });
    this.setupPortalPresence();
    await this.loadZonesFromDB();
    this.listen('colyseus:zones-changed', () => this.loadZonesFromDB());

    this.finishCreate();

    // Mobilier et murs créés via l'éditeur : chargés APRÈS finishCreate(),
    // qui crée le joueur local — leurs corps de collision (meubles solides,
    // murs) ne sont créés que s'il existe. Lancé avant, le fetch du mobilier
    // pouvait se résoudre pendant l'attente de loadZonesFromDB() ci-dessus
    // et les meubles solides devenaient traversables selon l'ordre des
    // réponses réseau (même cause que les portes, voir ensureDoorCollisionBodies).
    this.loadFurnitureFromDB();
    // Mobilier posé/retiré dans l'éditeur, en direct (relais furniture:{tenantId}:{mapId}) —
    // indispensable depuis que le campus reste ouvert derrière l'Admin.
    let furnitureReloadTimer: ReturnType<typeof setTimeout> | null = null;
    this.listen('colyseus:furniture-changed', () => {
      if (furnitureReloadTimer) return;
      furnitureReloadTimer = setTimeout(() => { furnitureReloadTimer = null; this.loadFurnitureFromDB(); }, 300);
    });
    this.sceneCleanups.push(() => { if (furnitureReloadTimer) clearTimeout(furnitureReloadTimer); });
    // Bureaux personnels : même raison (corps solides de leurs objets).
    void this.personalDesks?.load();
    // Murs rejoués en live sur 'colyseus:walls-changed' (relais du canal
    // Redis walls:{tenantId}:{mapId}).
    this.loadWallsFromDB();
    this.listen('colyseus:walls-changed', () => this.loadWallsFromDB());
    // Sols peints : un coup de pinceau dans l'éditeur = une requête → on
    // regroupe les rechargements (un par demi-seconde au plus).
    this.loadFloorsFromDB();
    const onFloorsChanged = () => {
      if (this.floorReloadTimer) return;
      this.floorReloadTimer = setTimeout(() => {
        this.floorReloadTimer = null;
        this.loadFloorsFromDB();
      }, 500);
    };
    this.listen('colyseus:floors-changed', onFloorsChanged);
    this.loadLabelsFromDB();
    const onLabelsChanged = () => this.loadLabelsFromDB();
    this.listen('colyseus:labels-changed', onLabelsChanged);
    this.sceneCleanups.push(() => {
      if (this.floorReloadTimer) clearTimeout(this.floorReloadTimer);
      this.floorReloadTimer = null;
    });
  }

  /**
   * Bulles de présence au-dessus des portails (portails TMJ et map_zones) :
   * redessinées quand la présence du campus change (sondage toutes les 20 s)
   * et quand les portails changent (loadZonesFromDB).
   */
  private setupPortalPresence(): void {
    const presence = new PortalPresence(this);
    this.portalPresence = presence;
    this.refreshPortalPresence();
    const unsubscribe = useGameStore.subscribe((state, prev) => {
      if (state.allZonePresence !== prev.allZonePresence) this.refreshPortalPresence();
    });
    this.sceneCleanups.push(() => {
      unsubscribe();
      presence.destroy();
      if (this.portalPresence === presence) this.portalPresence = null;
    });
  }

  private refreshPortalPresence(): void {
    if (!this.portalPresence || !this.zoneConfig) return;
    const byZone = new Map<string, PresencePerson[]>();
    for (const zone of useGameStore.getState().allZonePresence) {
      // Soi-même : la présence peut encore nous compter sur la carte quittée
      byZone.set(zone.zoneSlug, zone.players.filter((p) => p.userId !== this.localPlayerId));
    }
    // Pas de bulle au-dessus d'un portail vers un lieu réservé.
    const portals = this.zoneConfig.portals.filter((p) => canEnterZoneHere(p.targetZoneId.slice(p.targetZoneId.indexOf(':') + 1)));
    this.portalPresence.update(portals, byZone, this.zoneConfig.slug);
  }

  /** EventBus.on() retiré automatiquement à la fermeture de la scène. */
  private listen<K extends keyof GameEventMap>(event: K, fn: (data: GameEventMap[K]) => void): void {
    EventBus.on(event, fn);
    this.sceneCleanups.push(() => EventBus.off(event, fn));
  }

  private runSceneCleanups(): void {
    for (const cleanup of this.sceneCleanups.splice(0)) cleanup();
  }

  /**
   * Voile hors zone active. Zone éclairée, par priorité :
   *   1. la bulle / salle de réunion du joueur local — localBubbleId du store
   *      (état de la room Colyseus, calculé serveur avec hystérésis, y compris
   *      au join). Géométrie = GET /world/zones/:slug/bubble-zones, la même
   *      fusion TMJ + map_zones que le calcul serveur de bubbleId : les ids
   *      correspondent par construction ;
   *   2. sinon le bureau où il se tient (insideDeskId, détection client de
   *      checkDesks()).
   */
  /** Lumière d'ambiance : suit le réglage du store, la touche L le bascule. */
  private setupAmbientLight(): void {
    if (!AmbientLight.supported(this)) return;
    const light = new AmbientLight(this, {
      mapWidth: this.mapWidth,
      mapHeight: this.mapHeight,
      reduceMotion: () => useGameStore.getState().settings.reduceMotion,
    });
    this.ambientLight = light;
    light.setEnabled(useGameStore.getState().settings.ambientLight);
    const unsubscribe = useGameStore.subscribe((state, prev) => {
      if (state.settings.ambientLight !== prev.settings.ambientLight) light.setEnabled(state.settings.ambientLight);
    });
    this.sceneCleanups.push(() => {
      unsubscribe();
      light.destroy();
      if (this.ambientLight === light) this.ambientLight = null;
    });
  }

  private setupZoneDimmer(): void {
    const dimmer = new ZoneDimmer(this, this.mapWidth, this.mapHeight);
    this.zoneDimmer = dimmer;

    this.loadBubbleBounds();
    this.listen('colyseus:zones-changed', () => this.loadBubbleBounds());
    const unsubscribe = useGameStore.subscribe((state, prev) => {
      if (state.localBubbleId !== prev.localBubbleId) { this.updateDimmedArea(); this.syncOpenLockBarriers(); }
      if (state.localBubbleId !== prev.localBubbleId || state.localBubbleType !== prev.localBubbleType) this.updateRoomFraming();
    });
    // Salle revendiquée ou libérée : portes et murs invisibles suivent.
    const unsubscribeClaims = useRoomClaimsStore.subscribe((state, prev) => {
      if (state.claims !== prev.claims) this.syncOpenLockBarriers();
    });
    this.sceneCleanups.push(unsubscribeClaims);

    this.sceneCleanups.push(() => {
      unsubscribe();
      dimmer.destroy();
      if (this.zoneDimmer === dimmer) this.zoneDimmer = null;
    });
  }

  private updateDimmedArea(): void {
    if (!this.zoneDimmer) return;
    const bubbleId = useGameStore.getState().localBubbleId;
    const bubble = bubbleId ? this.bubbleBounds.get(bubbleId) : undefined;
    const desk = this.insideDeskId
      ? this.deskZones.find((d) => d.deskId === this.insideDeskId)?.bounds
      : undefined;
    // Zone inconnue (géométrie pas encore chargée) : jamais de voile sans
    // rien d'éclairé.
    this.zoneDimmer.setLitArea(bubble ?? desk ?? this.personalDesks?.litArea() ?? null);
  }

  private async goToMyDesk(): Promise<void> {
    const jwt = useGameStore.getState().jwt;
    if (!jwt || !this.zoneConfig) return;
    const toast = (message: string): void => { EventBus.emit('ui:toast', { message, tone: 'info' }); };
    const res = await fetch(`${API_BASE}/world/desks/personal/mine`, {
      headers: { Authorization: `Bearer ${jwt}`, 'X-Tenant-ID': getTenantSlug() },
    }).catch(() => null);
    if (!res?.ok) return toast('Ton bureau n’a pas pu être trouvé.');
    const desk = await res.json() as { mapId: string; tileX: number; tileY: number; width: number; height: number; outOfArea?: boolean } | null;
    if (!desk) return toast('Tu n’as pas encore de bureau : va sur un emplacement libre pour en prendre un.');
    if (desk.outOfArea) return toast('Ton bureau n’est plus dans son espace : choisis un nouvel emplacement.');
    const tile = 32;
    const x = (desk.tileX + desk.width / 2) * tile;
    const y = (desk.tileY + desk.height / 2) * tile;
    await this.goToPlace(desk.mapId, x, y, 'Aucun chemin ne mène à ton bureau d’ici.');
  }

  /** À pied jusqu'à un point d'une carte (bureau, salle d'une séance en cours), de carte en carte s'il le faut. */
  private async goToPlace(mapId: string, x: number, y: number, noPath: string): Promise<void> {
    const jwt = useGameStore.getState().jwt;
    if (!jwt || !this.zoneConfig) return;
    if (mapId === this.zoneConfig.slug) {
      walkRoute = null;
      if (!this.setMoveTarget(x, y)) EventBus.emit('ui:toast', { message: noPath, tone: 'info' });
      return;
    }
    walkRoute = { mapId, x, y, graph: await this.fetchPortalGraph(jwt) };
    this.walkRouteReplanUntil = 0;
    this.continueWalkRoute();
  }

  private async fetchPortalGraph(jwt: string): Promise<Array<{ from: string; to: string }>> {
    const res = await fetch(`${API_BASE}/world/portal-graph`, {
      headers: { Authorization: `Bearer ${jwt}`, 'X-Tenant-ID': getTenantSlug() },
    }).catch(() => null);
    return res?.ok ? await res.json() as Array<{ from: string; to: string }> : [];
  }

  /** « Rejoindre » une personne : à pied jusqu'à elle, de carte en carte s'il le faut. */
  private async goToPerson(userId: string, zoneSlug: string, name: string): Promise<void> {
    const jwt = useGameStore.getState().jwt;
    if (!jwt || !this.zoneConfig) return;
    this.personAim = null;
    this.personReplanAt = 0;
    if (zoneSlug === this.zoneConfig.slug) {
      walkRoute = { mapId: zoneSlug, x: 0, y: 0, graph: [], userId, name };
      this.personSearchUntil = this.time.now + PERSON_SEARCH_MS;
      return;
    }
    const graph = await this.fetchPortalGraph(jwt);
    walkRoute = { mapId: zoneSlug, x: 0, y: 0, graph, userId, name };
    this.walkRouteReplanUntil = 0;
    this.continueWalkRoute();
  }

  /** Avatar d'une personne présente sur cette carte, ou null. */
  private remoteAvatarOf(userId: string): CompositeAvatar | null {
    for (const [sessionId, uid] of this.sessionIdToUserId) {
      if (uid === userId) return this.remotePlayerSprites.get(sessionId) ?? null;
    }
    return null;
  }

  /**
   * « Rejoindre », sur la carte de la personne (chaque image) : marche vers son
   * avatar, recalculée quand elle bouge, arrêt une case à côté d'elle.
   */
  private followPerson(): void {
    const route = walkRoute;
    if (!route?.userId || route.mapId !== this.zoneConfig?.slug || !this.localPlayer) return;
    const toast = (message: string): void => { EventBus.emit('ui:toast', { message, tone: 'info' }); };
    const target = this.remoteAvatarOf(route.userId);
    if (!target) {
      if (this.time.now > this.personSearchUntil) {
        walkRoute = null;
        toast(`${route.name ?? 'Cette personne'} n’est plus sur cette carte.`);
      }
      return;
    }
    const body = this.localPlayer.body as Phaser.Physics.Arcade.Body | null;
    const from = body?.enable ? body.center : { x: this.localPlayer.x, y: this.localPlayer.y };
    if (Math.hypot(target.x - from.x, target.y - from.y) <= PERSON_REACH) {
      this.clearMoveTarget();
      walkRoute = null;
      return;
    }
    const aim = this.personAim;
    const moved = !aim || Math.hypot(target.x - aim.x, target.y - aim.y) > PERSON_REPLAN_DISTANCE;
    if ((this.moveTarget && !moved) || this.time.now < this.personReplanAt) return;
    this.personReplanAt = this.time.now + PERSON_REPLAN_MS;
    this.personAim = { x: target.x, y: target.y };
    if (!this.setMoveTarget(target.x, target.y)) {
      walkRoute = null;
      toast(`Aucun chemin ne mène à ${route.name ?? 'cette personne'} d’ici.`);
    }
  }

  /** Carte suivante sur le chemin le plus court (en passages) vers `to`, ou null. */
  private static nextMapTowards(graph: Array<{ from: string; to: string }>, from: string, to: string): string | null {
    const firstStep = new Map<string, string>();
    const queue: string[] = [from];
    const seen = new Set([from]);
    while (queue.length) {
      const cur = queue.shift()!;
      for (const e of graph) {
        if (e.from !== cur || seen.has(e.to)) continue;
        seen.add(e.to);
        firstStep.set(e.to, cur === from ? e.to : firstStep.get(cur)!);
        if (e.to === to) return firstStep.get(to)!;
        queue.push(e.to);
      }
    }
    return null;
  }

  /**
   * Avance d'une étape sur le trajet vers le bureau : sur sa carte, marche
   * jusqu'au bureau ; ailleurs, marche jusqu'au portail le plus proche vers la
   * carte suivante. Sans chemin à pied, dernier recours : passage direct sur
   * la carte du bureau, puis marche.
   */
  private continueWalkRoute(): void {
    const route = walkRoute;
    const slug = this.zoneConfig?.slug;
    if (!route || !slug || !this.localPlayer) return;
    if (slug === route.mapId) {
      this.walkRoutePortal = null;
      // Une personne : suivie par followPerson (son avatar arrive après la carte).
      if (route.userId) this.personSearchUntil = this.time.now + PERSON_SEARCH_MS;
      else this.setMoveTarget(route.x, route.y);
      return;
    }
    const next = ZoneScene.nextMapTowards(route.graph, slug, route.mapId);
    const body = this.localPlayer.body as Phaser.Physics.Arcade.Body | null;
    const from = body ? body.center : { x: this.localPlayer.x, y: this.localPlayer.y };
    const portals = next ? this.zoneConfig!.portals.filter((p) => p.targetZoneId.endsWith(`:${next}`)) : [];
    portals.sort((a, b) => this.distanceToRect(from, a.triggerBounds) - this.distanceToRect(from, b.triggerBounds));
    for (const portal of portals) {
      const r = portal.triggerBounds;
      if (this.setMoveTarget(r.x + r.width / 2, r.y + r.height / 2)) {
        this.walkRoutePortal = portal;
        return;
      }
    }
    // Aucun passage à pied : on rejoint la carte visée, la marche reprend à l'arrivée.
    this.walkRoutePortal = null;
    EventBus.emit('zone:transition-requested', { targetSlug: route.mapId, spawnId: 'default' });
  }

  private distanceToRect(p: { x: number; y: number }, r: { x: number; y: number; width: number; height: number }): number {
    const dx = Math.max(r.x - p.x, 0, p.x - (r.x + r.width));
    const dy = Math.max(r.y - p.y, 0, p.y - (r.y + r.height));
    return Math.hypot(dx, dy);
  }

  /** Recalcul de l'étape en cours après une arrivée (murs et mobilier chargés après coup). */
  private replanWalkRoute(): void {
    if (walkRoute && this.time.now < this.walkRouteReplanUntil) this.continueWalkRoute();
  }

  /** Fin de marche vers un portail du trajet sans l'avoir déclenché (case du portail bloquée) : on le franchit. */
  private onTapMoveEnded(): void {
    const portal = this.walkRoutePortal;
    if (!walkRoute || !portal || !this.localPlayer) return;
    const body = this.localPlayer.body as Phaser.Physics.Arcade.Body | null;
    const at = body ? body.center : { x: this.localPlayer.x, y: this.localPlayer.y };
    if (this.distanceToRect(at, portal.triggerBounds) > WALK_ROUTE_PORTAL_REACH) return;
    this.triggerZoneTransition(portal.targetZoneId, this.portalSpawnNames.get(portal.id));
  }

  private loadBubbleBounds(): void {
    const jwt = useGameStore.getState().jwt;
    const dimmer = this.zoneDimmer;
    if (!this.zoneConfig || !jwt || !dimmer) return;
    fetch(`${API_BASE}/world/zones/${encodeURIComponent(this.zoneConfig.slug)}/bubble-zones`, {
      headers: { Authorization: `Bearer ${jwt}`, 'X-Tenant-ID': getTenantSlug() },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<Array<{ id: string; bounds: LitArea; claimable?: boolean }>>;
      })
      .then((zones) => {
        if (this.zoneDimmer !== dimmer) return; // scène quittée entre-temps
        this.bubbleBounds = new Map(zones.map((z) => [z.id, z.bounds]));
        useRoomClaimsStore.getState().setClaimable(zones.filter((z) => z.claimable).map((z) => z.id));
        this.syncOpenLockBarriers();
        this.updateDimmedArea();
        this.updateRoomFraming();
      })
      .catch((err) => console.warn('[ZoneScene] bubble-zones (assombrissement) failed:', err));
  }

  /**
   * Crée les sprites AnimatedDoor pour une liste de portes (TMJ ou DB).
   * Extrait de finishCreate() pour être réutilisable par loadZonesFromDB()
   * qui ajoute des portes après coup, une fois le fetch admin résolu.
   */
  private spawnDoorSprites(doors: TmjDoor[]): void {
    const doorCatalog = doorsCatalog as DoorCatalogEntry[];
    this.ensureDoorCollisionBodies();
    for (const dz of doors) {
      if (!dz.doorSprite) continue;
      const entry = doorCatalog.find((e) => e.id === dz.doorSprite);
      if (!entry || !this.textures.exists(entry.id)) {
        console.warn(`[ZoneScene] doorSprite "${dz.doorSprite}" introuvable pour porte "${dz.name}"`);
        continue;
      }
      // Snapper la position sur la grille de tiles pour aligner avec les murs
      const tileW = this.tilemap?.tileWidth ?? 32;
      const tileH = this.tilemap?.tileHeight ?? 32;
      const snappedX = Math.floor(dz.bounds.x / tileW) * tileW;
      const snappedBottomY = Math.ceil((dz.bounds.y + dz.bounds.height) / tileH) * tileH;
      const sprite = new AnimatedDoor(
        this,
        snappedX,
        snappedBottomY,
        entry.id,
        entry.frames,
      );
      // Tri en Y comme les murs : un avatar au nord de la porte (y plus petit
      // que le bas de la porte) passe derrière son haut, au sud il passe devant.
      // Même règle que l'éditeur (EditorScene).
      sprite.setDepth(DEPTH_Y_BASE + snappedBottomY / DEPTH_Y_SCALE);
      this.add.existing(sprite);
      this.doorSprites.set(dz.id, sprite);
      console.log(`[ZoneScene] AnimatedDoor "${dz.name}" → sprite="${entry.id}" pos=(${dz.bounds.x}, ${dz.bounds.y + dz.bounds.height})`);
    }
  }

  /**
   * Corps de collision synthétiques des portes DB (voir doorCollisionBodies),
   * indépendants du sprite visuel choisi ou non. Idempotent.
   *
   * Appelé depuis spawnDoorSprites() ET après la création du joueur local
   * dans finishCreate() : au premier chargement, loadZonesFromDB() est
   * attendu AVANT finishCreate(), donc sans joueur local — les portes DB
   * n'avaient alors jamais de corps et on les traversait (bug trouvé en
   * testant le mode Portes de l'éditeur, 2026-09-24).
   */
  private ensureDoorCollisionBodies(): void {
    if (!this.localPlayer) return;
    for (const dz of this.doorZones) {
      if (!dz.id.startsWith('db-door-') || this.doorCollisionBodies.has(dz.id)) continue;
      const bodyX = dz.bounds.x + dz.bounds.width / 2;
      const bodyY = dz.bounds.y + dz.bounds.height / 2;
      const body = this.physics.add.staticImage(bodyX, bodyY, '__DEFAULT');
      body.setDisplaySize(dz.bounds.width, dz.bounds.height);
      body.setVisible(false);
      body.refreshBody();
      this.physics.add.collider(this.localPlayer, body);
      this.doorCollisionBodies.set(dz.id, body);
    }
  }

  /**
   * Zones créées via l'éditeur de carte (table map_zones), en complément
   * des zones du TMJ (parseZoneObjects). Miroir de loadFurnitureFromDB().
   *
   * Schémas de propriétés vérifiés contre ZonePropertiesModal
   * (apps/web/src/ui/admin/EditorPage.tsx) :
   *   - bubble : id = zone.name, aucune propriété requise côté client
   *     (bubbleId est calculé côté serveur, voir ZoneService.getBubbleZones —
   *     rien à faire ici, une zone bubble DB n'a pas de représentation
   *     cliente, seul le serveur Colyseus en a besoin).
   *   - door   : linkedBubble + doorSprite — mêmes clés que le format TMJ.
   *   - desk   : deskId + label — mêmes clés que le format TMJ.
   *   - portal : targetSlug + spawnId — même format que le TMJ (résolution
   *     par nom de spawn dans la zone cible, décision actée : plus précis
   *     que des coordonnées brutes).
   */
  private loadZonesFromDB(): Promise<void> {
    if (!this.zoneConfig) return Promise.resolve();
    const mapId = this.zoneConfig.slug;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return Promise.resolve();
    const tenantSlug = getTenantSlug();

    interface DBZone {
      id: string;
      type: 'desk' | 'bubble' | 'portal' | 'door' | 'spawn' | 'meeting-room' | 'desk-area' | 'elevator';
      name: string;
      properties: Record<string, unknown>;
      x: number;
      y: number;
      width: number;
      height: number;
    }

    // Rejouable (voir EventBus 'colyseus:zones-changed') : on retire d'abord
    // toutes les entrées DB de la précédente charge — sprites de porte inclus
    // — avant de repeupler. Les entrées TMJ (préfixe différent) ne sont
    // jamais touchées ici.
    for (const dz of this.doorZones) {
      if (!dz.id.startsWith('db-door-')) continue;
      this.doorSprites.get(dz.id)?.destroy();
      this.doorSprites.delete(dz.id);
      this.doorCollisionBodies.get(dz.id)?.destroy();
      this.doorCollisionBodies.delete(dz.id);
    }
    this.doorZones = this.doorZones.filter((d) => !d.id.startsWith('db-door-'));
    this.deskZones = this.deskZones.filter((d) => !d.id.startsWith('db-desk-'));
    if (this.zoneConfig) {
      for (const portalId of [...this.portalSpawnNames.keys()]) {
        if (portalId.startsWith('db-portal-')) this.portalSpawnNames.delete(portalId);
      }
      this.zoneConfig.portals = this.zoneConfig.portals.filter((p) => !p.id.startsWith('db-portal-'));
    }

    return fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/zones`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': tenantSlug,
      },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((zones: DBZone[]) => {
        const newDoors: TmjDoor[] = [];
        let portalsAdded = 0;
        const tenantId = this.zoneConfig!.tenantId;

        // Fix I-2 (Task 19) : vidée puis reconstruite à chaque rechargement
        // (même pattern que le filtrage des db-door-/db-desk-/db-portal-
        // ci-dessus) — sinon une bulle DB supprimée ou renommée entre deux
        // appels de loadZonesFromDB() (rejoué sur 'colyseus:zones-changed')
        // laisserait une entrée obsolète dans la map, et une porte encore
        // configurée avec l'ancien nom résoudrait vers un id de bulle
        // supprimé au lieu de retomber sur l'identité.
        this.bubbleNameToId.clear();
        this.bubbleIdToName.clear();
        const deskAreas: DeskArea[] = [];

        for (const z of zones) {
          // map_zones stocke des coordonnées en tuiles (même convention que
          // tileX/tileY du mobilier, voir EditorScene.ts) — convertir en
          // pixels pour matcher le format TMJ (bounds déjà en pixels).
          const bounds = {
            x: z.x * this.TILE_SIZE,
            y: z.y * this.TILE_SIZE,
            width: z.width * this.TILE_SIZE,
            height: z.height * this.TILE_SIZE,
          };

          if (z.type === 'door') {
            // linkedBubble vide = porte simple (s'ouvre/se ferme, jamais
            // verrouillée) — posée via le mode Portes de l'éditeur sans salle
            // adjacente. triggerDoorAction et setLocalBubbleHasDoor ignorent
            // déjà un id de bulle vide.
            const linkedBubble = (z.properties['linkedBubble'] as string | undefined) ?? '';
            const door: TmjDoor = {
              id: `db-door-${z.id}`,
              name: z.name,
              bounds,
              linkedBubble,
              // Résolu ci-dessous une fois bubbleNameToId construite (Fix I-2 / Task 19).
              resolvedBubbleId: linkedBubble,
              doorSprite: (z.properties['doorSprite'] as string | undefined) ?? null,
            };
            this.doorZones.push(door);
            newDoors.push(door);
          } else if (z.type === 'desk-area') {
            deskAreas.push({ id: z.id, name: z.name, x: z.x, y: z.y, width: z.width, height: z.height, properties: z.properties });
          } else if (z.type === 'desk') {
            const deskId = z.properties['deskId'] as string | undefined;
            if (!deskId) {
              console.warn(`[ZoneScene] DB desk "${z.name}" sans deskId — ignorée`);
              continue;
            }
            this.deskZones.push({
              id: `db-desk-${z.id}`,
              deskId,
              name: z.name,
              bounds,
              label: (z.properties['label'] as string | undefined) ?? `Bureau ${deskId}`,
            });
          } else if (z.type === 'portal') {
            const targetSlug = z.properties['targetSlug'] as string | undefined;
            if (!targetSlug) {
              console.warn(`[ZoneScene] DB portal "${z.name}" sans targetSlug — ignoré`);
              continue;
            }
            const spawnId = z.properties['spawnId'] as string | undefined;
            const portalId = `db-portal-${z.id}`;
            this.zoneConfig!.portals.push({
              id: portalId,
              targetZoneId: `${tenantId}:${targetSlug}`,
              triggerBounds: bounds,
              hysteresisBuffer: this.HYSTERESIS_BUFFER_PX,
            });
            if (spawnId) this.portalSpawnNames.set(portalId, spawnId);
            portalsAdded++;
            console.log(`[ZoneScene] DB portal "${z.name}" → slug="${targetSlug}" spawn="${spawnId ?? ''}" rect=(${bounds.x},${bounds.y},${bounds.width},${bounds.height})`);
          } else if (z.type === 'spawn') {
            // Même convention que parseZoneObjects() côté TMJ : centre du
            // rectangle. Écrase un spawn TMJ de même nom (permet à l'éditeur
            // de "remplacer" un spawn legacy sans toucher au fichier Tiled).
            this.spawnPoints.set(z.name, { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 });
          } else if (z.type === 'bubble' || z.type === 'meeting-room') {
            // Fix I-2 (Task 19) : table nom→id pour résoudre linkedBubble
            // (nom, saisi par un admin dans EditorPage.tsx) vers l'id stable
            // map_zones.id qui circule réellement en tant que bubbleId
            // (Task 13). Rien d'autre à faire côté client pour une zone
            // bubble elle-même, voir docstring de la méthode.
            // 'meeting-room' inclus (2026-08-23) : bubbleId est calculé de
            // façon identique pour les deux types côté serveur
            // (ZoneRoom.handleMove/getBubbleIdAtPosition ne distingue pas
            // bubble de meeting-room), donc une porte peut légitimement
            // verrouiller une meeting-room exactement comme une bulle
            // normale — exclure meeting-room ici aurait cassé le
            // verrouillage de porte pour ce type sans raison fonctionnelle.
            this.bubbleNameToId.set(z.name, z.id);
            this.bubbleIdToName.set(z.id, z.name);
          }
        }

        useGameStore.getState().setBubbleNames(new Map(this.bubbleIdToName));

        // Fix I-2 (Task 19) : résout linkedBubble (nom) → id de bulle réel,
        // UNIQUEMENT pour les portes créées via l'éditeur (préfixe
        // "db-door-", même convention que db-desk-/db-portal- ci-dessus).
        // Les portes TMJ ne doivent JAMAIS être résolues via bubbleNameToId :
        // rien ne garantit l'unicité de nom entre une bulle TMJ et une bulle
        // DB (seul un chevauchement de rectangle est vérifié à la création,
        // pas le nom) — une collision de nom redirigerait alors
        // silencieusement une porte TMJ, qui pointait correctement vers sa
        // bulle TMJ par identité (id === name), vers l'UUID d'une bulle DB
        // sans rapport. Les portes TMJ gardent donc resolvedBubbleId ===
        // linkedBubble tel qu'initialisé dans parseZoneObjects().
        for (const d of this.doorZones) {
          if (!d.id.startsWith('db-door-')) continue;
          d.resolvedBubbleId = this.bubbleNameToId.get(d.linkedBubble) ?? d.linkedBubble;
        }

        if (newDoors.length > 0) this.spawnDoorSprites(newDoors);
        // Ascenseurs : sprites, étages du groupe, arrivée devant chacun (sauf point d'arrivée posé à la main).
        for (const [name, point] of this.ensureElevators().setZones(zones)) {
          if (!this.spawnPoints.has(name)) this.spawnPoints.set(name, point);
        }
        this.personalDesks?.setAreas(deskAreas);

        // Le joueur peut déjà être dans une bulle dont l'ensemble de portes
        // vient de changer (porte ajoutée/retirée pendant qu'il y est) —
        // recalculer plutôt que d'attendre le prochain bubble:changed.
        const currentBubbleId = useGameStore.getState().localBubbleId;
        if (currentBubbleId) {
          const hasDoor = this.doorZones.some((d) => d.resolvedBubbleId === currentBubbleId);
          useGameStore.getState().setLocalBubbleHasDoor(hasDoor);
        }

        this.refreshPortalPresence();
        console.log(`[ZoneScene] loadZonesFromDB — ${zones.length} zones DB (${newDoors.length} portes, ${this.deskZones.length} bureaux au total, ${portalsAdded} portails)`);
      })
      .catch((err) => console.warn('[ZoneScene] loadZonesFromDB failed:', err));
  }

  /** Suite commune de create() apres le chargement de la map (ou fallback). */
  private finishCreate(): void {
    // ── 3. Animations avatars ─────────────────────────────────
    registerAvatarAnimations(this);

    // ── 4. Joueur local ───────────────────────────────────────

    // Position resumée après un aller-retour panneau admin (même zone) —
    // prioritaire sur le spawn TMJ. Consommée une seule fois : un vrai
    // changement de zone ou un rechargement de page retombe normalement
    // sur targetSpawnName / "default".
    const lastPosition = useGameStore.getState().lastPlayerPosition;
    const resumingHere = lastPosition?.zoneSlug === this.zoneConfig?.slug ? lastPosition : null;
    if (resumingHere) useGameStore.getState().setLastPlayerPosition(null);

    // Task 2 : spawn depuis le TMJ — targetSpawnName du store, sinon "default", sinon centre
    const targetSpawnName = useGameStore.getState().targetSpawnName;
    const namedSpawn = targetSpawnName
      ? this.spawnPoints.get(targetSpawnName)
      : this.spawnPoints.get('default');
    const spawnX = resumingHere?.x ?? namedSpawn?.x ?? this.mapWidth  / 2;
    const spawnY = resumingHere?.y ?? namedSpawn?.y ?? this.mapHeight / 2;
    console.log('[ZoneScene] spawnPoints:', [...this.spawnPoints.entries()]);
    console.log('[ZoneScene] targetSpawnName:', targetSpawnName);
    console.log('[ZoneScene] spawn choisi:', resumingHere ? `position reprise → (${spawnX}, ${spawnY})` : namedSpawn ? `"${targetSpawnName ?? 'default'}" → (${spawnX}, ${spawnY})` : `fallback centre (${spawnX}, ${spawnY})`);

    const localDisplayName  = useGameStore.getState().displayName ?? '';
    const localAppearance   = useGameStore.getState().appearance ?? DEFAULT_APPEARANCE;
    this.localPlayer = new CompositeAvatar(this, spawnX, spawnY, localDisplayName);
    // Depth set dynamically in sortDepths()
    this.localPlayer.setStatus(useGameStore.getState().localStatus);
    this.localPlayer.updateAppearance(localAppearance);

    // Dernière place retenue (lib/lastPlace) : reprise en revenant dans l'heure.
    const rememberPlace = () => {
      const slug = this.zoneConfig?.slug;
      if (!slug || !this.localPlayer) return;
      saveLastPlace({ zoneSlug: slug, x: Math.round(this.localPlayer.x), y: Math.round(this.localPlayer.y) });
    };
    const placeTimer = this.time.addEvent({ delay: 5000, loop: true, callback: rememberPlace });
    const rememberOnHide = () => { if (document.visibilityState === 'hidden') rememberPlace(); };
    window.addEventListener('pagehide', rememberPlace);
    document.addEventListener('visibilitychange', rememberOnHide);
    this.sceneCleanups.push(() => {
      placeTimer.remove();
      window.removeEventListener('pagehide', rememberPlace);
      document.removeEventListener('visibilitychange', rememberOnHide);
    });
    this.localPlayer.playCompositeAnimation('idle_down');

    // ── Task 1 : Arcade Physics sur le joueur local ───────────
    this.physics.add.existing(this.localPlayer);
    const body = this.localPlayer.body as Phaser.Physics.Arcade.Body;
    // Corps = les pieds seulement : 16 × 10 px (taille source 8 × 5, échelle 2),
    // centré sur l'ancre en x, de 9 px au-dessus des pieds à 1 px dessous.
    // Avant (2026-10-21) : 28 × 28 px sur tout le bas du corps — une porte d'une
    // case ne laissait que 2 px de marge et l'avatar butait loin des objets.
    // Le sprite d'ancre est la texture __DEFAULT (32 × 32) à l'échelle 2 :
    // centre = ancre + 2 × (offset − 16) + demi-taille.
    body.setSize(PLAYER_BODY.w / 2, PLAYER_BODY.h / 2);
    body.setOffset(16 - PLAYER_BODY.w / 4, 16 - (PLAYER_BODY.h - PLAYER_BODY.lift) / 2);
    body.setCollideWorldBounds(true);
    this.physics.world.setBounds(0, 0, this.mapWidth, this.mapHeight);

    for (const layer of this.collisionLayers) {
      this.physics.add.collider(this.localPlayer, layer);
    }
    this.ensureDoorCollisionBodies();

    // Mise a jour du statut local en temps reel
    this.listen('ui:status-change', ({ status }: { status: string }) => {
      this.localPlayer?.setStatus(status);
    });

    // ── 4a. Assombrissement hors zone active ──────────────────
    this.setupZoneDimmer();
    this.setupAmbientLight();

    // ── 4b. Bulle de proximite visuelle ───────────────────────
    this.proximityGraphics = this.add.graphics();
    this.proximityGraphics.setDepth(DEPTH_PROXIMITY);

    this.listen('bubble:changed', ({ previousBubbleId, newBubbleId }: { previousBubbleId: string; previousType: string; newBubbleId: string; newType: string }) => {
      this.localBubbleActive = newBubbleId !== '';

      // Verrouiller une bulle sans porte n'a pas de sens (pas de mur, pas de
      // "toc") — le cadenas (LockButton) ne doit s'afficher que si la bulle
      // actuelle a au moins une porte qui la référence via linkedBubble.
      // doorZones n'existe que côté ZoneScene (Phaser), d'où l'écriture
      // directe au store ici plutôt qu'un aller-retour EventBus/GameBridge.
      const hasDoor = newBubbleId !== '' && this.doorZones.some((d) => d.resolvedBubbleId === newBubbleId);
      useGameStore.getState().setLocalBubbleHasDoor(hasDoor);

      // Révoquer l'accès invité quand le joueur sort d'une bulle verrouillée
      // (sauf si le joueur est owner du verrou)
      if (previousBubbleId && previousBubbleId !== newBubbleId
          && this.allowedBubbleIds.has(previousBubbleId)
          && this.lockedBubbleIds.has(previousBubbleId)) {
        const owners = useGameStore.getState().lockedBubbleOwners.get(previousBubbleId);
        const isOwner = !!(owners && this.localPlayerId && owners.has(this.localPlayerId));
        if (!isOwner) {
          this.allowedBubbleIds.delete(previousBubbleId);
        }
      }
    });
    this.listen('proximity:changed', ({ nearbyUserIds }: { nearbyUserIds: string[] }) => {
      this.proximityNearbyCount = nearbyUserIds.length;
    });

    // ── 5. Camera ─────────────────────────────────────────────

    let savedZoom = useGameStore.getState().zoomLevel;
    const beforeFit = readZoomBeforeFit();
    if (beforeFit && savedZoom === beforeFit.fitted) savedZoom = beforeFit.pref;
    this.cameras.main.startFollow(this.localPlayer, true, 0.12, 0.12);
    this.cameraFollowing = this.localPlayer;
    const fitted = Math.max(savedZoom, this.minZoomForMap());
    writeZoomBeforeFit(fitted !== savedZoom ? { pref: savedZoom, fitted } : null);
    this.applyZoom(fitted);
    const onResize = () => this.applyZoom(this.cameras.main.zoom);
    this.scale.on(Phaser.Scale.Events.RESIZE, onResize);
    this.sceneCleanups.push(() => this.scale.off(Phaser.Scale.Events.RESIZE, onResize));
    // Panneau latéral, vignettes vidéo : limites recalculées (voir hudInsets).
    this.listen('ui:hud-insets-changed', () => this.onHudInsetsChanged());

    // ── 6. Input ──────────────────────────────────────────────

    this.cursors = this.input.keyboard!.createCursorKeys();

    const kb = this.input.keyboard!;
    this.heldMoveCodes.clear();
    // Touches de déplacement suivies directement sur la fenêtre (phase de
    // capture) : jamais dépendantes de la file d'événements de Phaser, qui
    // pouvait laisser passer un relâchement (touche tenue pour toujours,
    // l'avatar ne s'arrêtait plus) ou ignorer les flèches à l'arrivée.
    const onMoveKeyDown = (e: KeyboardEvent) => {
      if (!MOVE_CODES.has(e.code) || this.chatInputActive || isTextField(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return; // raccourci (Cmd+flèche…), pas un déplacement
      this.heldMoveCodes.add(e.code);
    };
    const onMoveKeyUp = (e: KeyboardEvent) => {
      this.heldMoveCodes.delete(e.code);
      // macOS n'envoie pas le relâchement d'une touche tenue avec Cmd : tout relâcher.
      if (e.key === 'Meta') this.heldMoveCodes.clear();
    };
    const releaseOnHide = () => { if (document.hidden) this.heldMoveCodes.clear(); };
    window.addEventListener('keydown', onMoveKeyDown, true);
    window.addEventListener('keyup', onMoveKeyUp, true);
    document.addEventListener('visibilitychange', releaseOnHide);
    this.sceneCleanups.push(() => {
      window.removeEventListener('keydown', onMoveKeyDown, true);
      window.removeEventListener('keyup', onMoveKeyUp, true);
      document.removeEventListener('visibilitychange', releaseOnHide);
    });
    kb.on('keydown', (e: KeyboardEvent) => {
      // Zoom : par caractère produit (`e.key`), pas par keyCode — le « - »
      // AZERTY est sur la touche 6 et le « = » ailleurs qu'en QWERTY.
      if (this.chatInputActive || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === '+' || e.key === '=') this.stepZoom(1);
      else if (e.key === '-') this.stepZoom(-1);
      else if ((e.key === 'l' || e.key === 'L') && this.ambientLight) {
        const { settings, updateSettings } = useGameStore.getState();
        updateSettings({ ambientLight: !settings.ambientLight });
      }
    });
    // Fenêtre quittée touche enfoncée : le keyup n'arrivera jamais.
    const releaseMoveKeys = () => this.heldMoveCodes.clear();
    this.game.events.on(Phaser.Core.Events.BLUR, releaseMoveKeys);
    this.sceneCleanups.push(() => this.game.events.off(Phaser.Core.Events.BLUR, releaseMoveKeys));

    this.keySpace = kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.keyE = kb.addKey(Phaser.Input.Keyboard.KeyCodes.E);
    this.keyShift = kb.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);

    // Molette et pincement du pavé tactile : zoom continu.
    this.sceneCleanups.push(attachWheelZoom(this.game.canvas, {
      getZoom: () => this.cameras.main.zoom,
      setZoom: (level) => this.applyZoom(level),
      min: ZOOM_MIN,
      max: ZOOM_MAX,
      isEnabled: () => !this.chatInputActive,
    }));

    // ── Pan caméra manuel (clic maintenu + glisser à la souris) ──
    // Ne démarre le pan qu'après un vrai glisser (seuil en px) pour ne pas
    // gêner le double-clic sur un avatar distant (wave) ni un simple clic.
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (this.chatInputActive || !p.leftButtonDown()) return;
      this.panPointerDownX = p.x;
      this.panPointerDownY = p.y;
    });

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.leftButtonDown() && !this.isPanningCamera) {
        const world = this.cameras.main.getWorldPoint(p.x, p.y);
        const over = this.remoteAvatarAt(world.x, world.y) !== null;
        if (over !== this.hoveringAvatar) {
          this.hoveringAvatar = over;
          this.input.setDefaultCursor(over ? 'pointer' : 'default');
        }
      }
      if (this.chatInputActive || !p.leftButtonDown()) return;
      if (!this.isPanningCamera) {
        const dragDist = Phaser.Math.Distance.Between(this.panPointerDownX, this.panPointerDownY, p.x, p.y);
        if (dragDist < this.PAN_DRAG_THRESHOLD_PX) return;
        this.isPanningCamera = true;
        this.cameraDetached = true;
        this.cameras.main.stopFollow();
        this.cameraFollowing = null;
        // Capturé ICI (pas au pointerdown) : le follow était encore actif entre le
        // clic et le franchissement du seuil, donc le scroll a pu bouger entre-temps
        // — capturer une valeur périmée provoquait un saut de la caméra au démarrage du pan.
        this.panStartScrollX = this.cameras.main.scrollX;
        this.panStartScrollY = this.cameras.main.scrollY;
        this.cameras.main.panEffect.reset(); // annule un glissé de retour en cours (voir handleInput)
        this.input.setDefaultCursor('grabbing');
      }
      const zoom = this.cameras.main.zoom;
      this.cameras.main.scrollX = this.panStartScrollX - (p.x - this.panPointerDownX) / zoom;
      this.cameras.main.scrollY = this.panStartScrollY - (p.y - this.panPointerDownY) / zoom;
    });

    this.input.on('pointerup', () => {
      if (!this.isPanningCamera) return;
      this.isPanningCamera = false;
      this.input.setDefaultCursor('default');
    });

    // ── Double-clic souris sur la carte = y aller (même itinéraire que le tap-to-move) ──
    // Pas sur un objet interactif (double-clic sur un avatar = coucou), ni pendant
    // la pose ou l'aménagement d'un bureau (le clic y pose des objets), ni après un glisser.
    this.input.on('pointerup', (p: Phaser.Input.Pointer, over: Phaser.GameObjects.GameObject[]) => {
      if (p.wasTouch || p.button !== 0 || this.chatInputActive) return;
      // Relâché ailleurs que sur la carte (tableau blanc en demi-écran, fenêtre…).
      if (p.upElement && p.upElement !== this.game.canvas) { this.lastMapClick = null; return; }
      const dragged = Phaser.Math.Distance.Between(this.panPointerDownX, this.panPointerDownY, p.x, p.y) >= this.PAN_DRAG_THRESHOLD_PX;
      const desk = useGameStore.getState().personalDeskUi;
      if (dragged || desk.customizing || desk.placing) { this.lastMapClick = null; return; }
      const now = Date.now();
      // Double-clic sur un avatar = coucou (pas un déplacement).
      const clicked = this.cameras.main.getWorldPoint(p.x, p.y);
      const avatarSession = this.remoteAvatarAt(clicked.x, clicked.y);
      if (avatarSession) {
        this.lastMapClick = null;
        if (this.lastClickSessionId === avatarSession && now - this.lastClickTime < this.DOUBLE_CLICK_MS) {
          this.lastClickSessionId = null;
          const userId = this.sessionIdToUserId.get(avatarSession);
          if (userId) EventBus.emit('ui:wave-send', { targetUserId: userId });
        } else {
          this.lastClickSessionId = avatarSession;
          this.lastClickTime = now;
        }
        return;
      }
      this.lastClickSessionId = null;
      if (over.length > 0) { this.lastMapClick = null; return; }
      const last = this.lastMapClick;
      if (last && now - last.time < this.DOUBLE_CLICK_MS && Phaser.Math.Distance.Between(last.x, last.y, p.x, p.y) < 8) {
        this.lastMapClick = null;
        const world = this.cameras.main.getWorldPoint(p.x, p.y);
        walkRoute = null;
        this.setMoveTarget(world.x, world.y);
      } else {
        this.lastMapClick = { time: now, x: p.x, y: p.y };
      }
    });

    // Marqueur de destination tap-to-move (tactile)
    this.moveTargetMarker = this.add.graphics();
    this.moveTargetMarker.setDepth(DEPTH_MOVE_TARGET);

    // ── 7. Prechargement zone voisine (best-effort) ───────────

    if (this.zoneConfig && this.zoneUrls) {
      const firstPortal = this.zoneConfig.portals[0];
      if (firstPortal) {
        const [adjTenantId, adjSlug] = firstPortal.targetZoneId.split(':');
        if (adjTenantId && adjSlug) {
          const jwt = useGameStore.getState().jwt ?? '';
          const adjUrls = buildZoneAssetUrls(adjTenantId, adjSlug, this.zoneUrls.sprites.avatars);
          const adjConfig: ZoneConfig = {
            id: firstPortal.targetZoneId, tenantId: adjTenantId, slug: adjSlug,
            displayName: '', tmjPath: '', wamPath: '', portals: [], maxOccupants: 0,
          };
          void this.zoneLoader.prefetchAdjacentZone(adjConfig, adjUrls, jwt);
        }
      }
    }

    // ── 7b. Suspendre / reprendre input Phaser selon l'overlay chat ou modal ──
    // Deux causes indépendantes : une fenêtre ouverte (ui:modal-*) et une saisie
    // dans un panneau latéral (ui:chat-*, focus d'un champ de texte). Le jeu ne
    // reprend la main que quand aucune des deux n'est active.
    let modalOpen = false;
    let typing = false;
    const applyInputBlock = () => {
      this.chatInputActive = modalOpen || typing;
      if (this.chatInputActive) {
        this.heldMoveCodes.clear(); // une touche tenue ne doit pas continuer à faire marcher
        this.input.keyboard?.disableGlobalCapture();
      } else {
        this.input.keyboard?.enableGlobalCapture();
      }
    };
    this.listen('ui:chat-open', () => { typing = true; applyInputBlock(); });
    this.listen('ui:chat-close', () => {
      if (useGameStore.getState().adminPage) return; // administration ouverte par-dessus
      typing = false;
      applyInputBlock();
    });
    this.listen('ui:modal-opened', () => { modalOpen = true; applyInputBlock(); });
    this.listen('ui:modal-closed', () => {
      // Administration ouverte par-dessus : seul son retour rend la main au jeu
      // (une fenêtre du campus qui se ferme entre-temps ne doit pas le faire).
      if (useGameStore.getState().adminPage) return;
      modalOpen = false;
      applyInputBlock();
    });

    // ── 7c. Tap-to-move / bouton d'action tactile ──────────────
    // « Aller à mon bureau » : à pied sur cette carte (une téléportation serait
    // refusée par l'anti-téléportation du serveur), sinon changement de carte
    // avec arrivée au centre du bureau (même chemin que l'arrivée à la connexion).
    this.listen('ui:go-to-desk', () => { void this.goToMyDesk(); });
    this.listen('ui:go-to-person', ({ userId, zoneSlug, name }: { userId: string; zoneSlug: string; name: string }) => {
      void this.goToPerson(userId, zoneSlug, name);
    });
    // Bandeau « Coaching en cours » : à pied jusqu'à la salle.
    this.listen('ui:go-to-place', ({ zoneSlug, x, y, name }: { zoneSlug: string; x: number; y: number; name: string }) => {
      void this.goToPlace(zoneSlug, x, y, `Aucun chemin ne mène à ${name} d’ici.`);
    });
    this.listen('input:tap-move', ({ clientX, clientY }: { clientX: number; clientY: number }) => {
      const canvasRect = this.game.canvas.getBoundingClientRect();
      const localX = clientX - canvasRect.left;
      const localY = clientY - canvasRect.top;
      const world = this.cameras.main.getWorldPoint(localX, localY);
      walkRoute = null;
      this.setMoveTarget(world.x, world.y);
    });
    this.listen('input:action-press', () => {
      this.triggerAction();
    });

    // ── 8. Badge MUTE sur les avatars en temps reel ────────────
    this.listen('moderation:player-muted', ({ userId, durationMs }: { userId: string; durationMs?: number }) => {
      this.remotePlayerSprites.forEach((sprite, sessionId) => {
        if (this.sessionIdToUserId.get(sessionId) === userId) {
          sprite.setMuted(true, durationMs);
        }
      });
    });
    this.listen('moderation:player-unmuted', ({ userId }: { userId: string }) => {
      this.remotePlayerSprites.forEach((sprite, sessionId) => {
        if (this.sessionIdToUserId.get(sessionId) === userId) {
          sprite.setMuted(false);
        }
      });
    });

    // ── 9. Relance BootScene sur transition inter-zones ────────
    this.listen('game:restart-scene', this.onRestartScene);

    // ── 10a. Capture pour un retour (« Signaler un problème ») ──
    // Le canvas seul : les fenêtres React et les vidéos (DOM) n'y sont pas.
    this.listen('ui:feedback-capture', ({ reply }) => {
      const base = {
        ...(this.zoneConfig ? { map: this.zoneConfig.slug } : {}),
        ...(this.localPlayer ? { tileX: Math.floor(this.localPlayer.x / this.TILE_SIZE), tileY: Math.floor(this.localPlayer.y / this.TILE_SIZE) } : {}),
      };
      try {
        this.game.renderer.snapshot((image) => {
          if (!(image instanceof HTMLImageElement) || !image.naturalWidth) { reply({ ...base, dataUrl: null }); return; }
          const scale = Math.min(1, 1600 / image.naturalWidth);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(image.naturalWidth * scale);
          canvas.height = Math.round(image.naturalHeight * scale);
          canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
          reply({ ...base, dataUrl: canvas.toDataURL('image/jpeg', 0.85) });
        }, 'image/png');
      } catch {
        reply({ ...base, dataUrl: null });
      }
    });

    // ── 10b. Zoom depuis le HUD ──────────────────────────────
    this.listen('ui:zoom-change', ({ level }: { level: number }) => {
      this.applyZoom(level);
    });

    // ── 10c. Réactions emoji ─────────────────────────────────
    this.reactions = new ReactionEffects(this, DEPTH_REACTION, (userId) => this.findSpriteByUserId(userId));
    this.sceneCleanups.push(() => { this.reactions?.destroy(); this.reactions = null; });
    this.listen('reaction:received', ({ userId, emoji }: { userId: string; emoji: string }) => {
      this.reactions?.show(userId, emoji);
    });

    // ── 10d. Indicateur de parole (speaker ping) ─────────────
    this.listen('livekit:speakers-changed', ({ speakerUserIds }: { speakerUserIds: string[] }) => {
      this.updateSpeakerPings(speakerUserIds);
    });

    // ── 10e. Profil mis à jour (local OU distant) ──────────
    this.listen('player:profile-updated', (data: { sessionId: string; userId: string; displayName: string; avatarIndex: number }) => {
      console.log('[ZoneScene] profile-updated reçu:', data.userId, 'local?', data.userId === this.localPlayerId);
      if (data.userId === this.localPlayerId && this.localPlayer) {
        this.localPlayer.setDisplayName(data.displayName);
      } else {
        const sprite = this.remotePlayerSprites.get(data.sessionId);
        if (sprite) {
          sprite.setDisplayName(data.displayName);
        }
      }
    });

    // ── 10e2. Appearance mise à jour (distant) ──────────────
    this.listen('player:appearance-updated', (data: { sessionId: string; userId: string; appearanceJson: string }) => {
      try {
        const appearance = JSON.parse(data.appearanceJson);
        if (data.userId === this.localPlayerId && this.localPlayer) {
          loadAppearanceIfNeeded(this, appearance, () => {
            this.localPlayer?.updateAppearance(appearance);
          });
        } else {
          const sprite = this.remotePlayerSprites.get(data.sessionId);
          if (sprite) {
            loadAppearanceIfNeeded(this, appearance, () => {
              sprite.updateAppearance(appearance);
            });
          }
        }
      } catch { /* invalid JSON — ignore */ }
    });

    // ── 10f. Verrouillage bulles côté client ────────────────
    this.listen('bubble:lock-changed', ({ bubbleId, locked }: { bubbleId: string; locked: boolean }) => {
      if (locked) this.lockedBubbleIds.add(bubbleId);
      else {
        this.lockedBubbleIds.delete(bubbleId);
        this.allowedBubbleIds.delete(bubbleId);
      }
      console.log('[ZoneScene] bubble:lock-changed — lockedBubbleIds:', [...this.lockedBubbleIds]);
      this.syncOpenLockBarriers();
    });
    this.listen('knock:granted', ({ bubbleId }: { bubbleId: string }) => {
      this.allowedBubbleIds.add(bubbleId);
      this.syncOpenLockBarriers();
    });

    // ── Sprint 11a : Labels des bureaux ──────────────────────────
    this.loadDeskAssignments();

    // ── Sprint 11d : Spotlight podium ───────────────────────────
    this.podiumSpotlight = this.add.graphics();
    this.podiumSpotlight.setDepth(DEPTH_SPOTLIGHT);
    this.listen('podium:state', ({ speakerId }: { speakerId: string | null }) => {
      // Force redraw on speaker change
      if (!speakerId && this.podiumSpotlight) {
        this.podiumSpotlight.clear();
      }
    });

    // Arrivée par un portail : la carte s'ouvre en fondu depuis le noir
    if (fadeInOnArrival) {
      fadeInOnArrival = false;
      this.cameras.main.fadeIn(PORTAL_FADE_MS, 0, 0, 0);
    }
    if (walkRoute) {
      this.walkRouteReplanUntil = this.time.now + WALK_ROUTE_REPLAN_MS;
      this.continueWalkRoute();
      // Arrivé sur la carte du bureau : le trajet s'arrête après le dernier recalcul
      // (vers une personne, il continue jusqu'à elle : followPerson).
      if (walkRoute?.mapId === this.zoneConfig?.slug && !walkRoute.userId) {
        const route = walkRoute;
        this.time.delayedCall(WALK_ROUTE_REPLAN_MS, () => { if (walkRoute === route) walkRoute = null; });
      }
    }
    // Arrivée par l'ascenseur : les portes s'ouvrent sur l'avatar, qui sort de la cabine.
    const arrivalZoneId = this.elevators?.arrive(targetSpawnName);
    if (arrivalZoneId) this.arriveByElevator(arrivalZoneId);

    // ── 11. Notifier React ─────────────────────────────────────

    EventBus.emit('zone:entered', {
      zoneId:   this.zoneConfig?.id   ?? '',
      zoneName: this.zoneConfig?.displayName ?? '',
      spawnX:   this.localPlayer?.x ?? 0,
      spawnY:   this.localPlayer?.y ?? 0,
    });
    EventBus.emit('scene:ready', { scene: this, sceneKey: 'ZoneScene' });
  }

  // ── ME-4d: Load admin-placed furniture from DB ─────────────

  private readonly TILE_SIZE = 32;

  private loadFurnitureFromDB(): void {
    if (!this.zoneConfig) return;
    const mapId = this.zoneConfig.slug;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    const tenantSlug = getTenantSlug();

    interface FurnitureItem {
      id: string;
      furnitureId: string;
      tileX: number;
      tileY: number;
      layer: string;
      isSolid: boolean;
      collisionGrid?: unknown;
    }

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/furniture`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': tenantSlug,
      },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((stored: FurnitureItem[]) => {
        // Objets debout (plantes, lampadaires, fontaines…) : base bloquante et
        // tri en Y imposés par l'objet, quel que soit leur réglage enregistré.
        const items = stored.map((item) => standingPlacement(findFurniture(item.furnitureId), item));
        // Emprise des objets « au sol » et des ponts/pontons (walkable, quel
        // que soit leur calque — posés en « Mobilier » avant le 2026-09-28) :
        // la case (tileX, tileY) est celle du bas à gauche (même ancrage que
        // les corps solides, spawnFurnitureItem).
        this.floorObjectCells.clear();
        for (const item of items) {
          const entry = findFurniture(item.furnitureId);
          if (!entry || (item.layer !== 'floor' && !entry.walkable)) continue;
          const w = Math.max(1, Math.round(entry.dimensions.w / this.TILE_SIZE));
          const h = Math.max(1, Math.round(entry.dimensions.h / this.TILE_SIZE));
          for (let dy = 0; dy < h; dy++) {
            for (let dx = 0; dx < w; dx++) {
              // walkableTiles : rangées du haut vers le bas ; dy compte depuis le bas.
              if (entry.walkableTiles?.[h - 1 - dy]?.[dx] === '0') continue;
              this.floorObjectCells.add((item.tileY - dy) * MAX_FLOOR_DIMENSION + item.tileX + dx);
            }
          }
        }
        this.rebuildWaterBodies();

        // Textures à la demande (images du catalogue, pages des planches pour
        // les tampons) — jamais tout le catalogue côté jeu.
        const entries = items.map((item) => findFurniture(item.furnitureId));
        items.forEach((item, i) => {
          if (!entries[i]) console.warn(`[ZoneScene] furniture not in catalog: ${item.furnitureId}`);
        });
        const generation = ++this.furnitureGeneration;
        void ensureFurnitureTextures(this, entries.filter((e) => e !== undefined)).then(() => {
          // Un rechargement plus récent est parti entre-temps, ou la scène est fermée.
          if (generation !== this.furnitureGeneration || !this.sys?.displayList) return;
          for (const old of this.dbFurniture) {
            old.sprite.destroy();
            for (const b of old.bodies) b.destroy();
            this.furnitureBodies.delete(old.id);
            this.forgetSeat(old.id);
          }
          this.dbFurniture = [];
          for (const item of items) {
            if (!findFurniture(item.furnitureId)) continue;
            const spawned = this.spawnFurnitureItem(item);
            if (spawned) this.dbFurniture.push({ id: item.id, item, ...spawned });
          }
          this.scooters?.setPlaced(this.dbFurniture);
          this.ensureLocalPlayerOnFreeCell();
          this.replanWalkRoute();
          console.log(`[ZoneScene] loaded ${items.length} furniture from DB`);
        });
      })
      .catch((err) => console.warn('[ZoneScene] load furniture failed:', err));
  }

  /** Affiche un meuble et crée ses corps solides ; retourné pour les objets qu'on retire (bureaux personnels). */
  /**
   * `usable: false` : objet du bureau personnel d'un autre membre (`canUseDeskItem`) —
   * affiché et solide, mais aucune interaction enregistrée (siège, et toute
   * interaction à venir sur un meuble : à brancher sous ce même drapeau).
   */
  private spawnFurnitureItem(item: { id: string; furnitureId: string; tileX: number; tileY: number; layer: string; isSolid: boolean; collisionGrid?: unknown }, usable = true): { sprite: Phaser.GameObjects.Image; bodies: Phaser.Physics.Arcade.Image[] } | null {
    let sprite: Phaser.GameObjects.Image;
    try {
      sprite = this.zoneLoader.spawnFurniture(item.furnitureId, item.tileX, item.tileY, item.layer);
    } catch {
      console.warn(`[ZoneScene] furniture catalog miss: ${item.furnitureId}`);
      return null;
    }
    let bodies: Phaser.Physics.Arcade.Image[] = [];

    if (item.isSolid && this.localPlayer) {
      const ts = this.TILE_SIZE;
      const bottom = (item.tileY + 1) * ts;
      const addBody = (x: number, y: number, w: number, h: number) => {
        const body = this.physics.add.staticImage(x + w / 2, y + h / 2, '__DEFAULT');
        body.setDisplaySize(w, h);
        body.setVisible(false);
        body.refreshBody();
        this.physics.add.collider(this.localPlayer!, body);
        bodies.push(body);
      };
      if (isValidCollisionGrid(item.collisionGrid)) {
        // Grille (ex. « la base » seulement) : un corps par suite de cases
        // bloquantes sur une rangée. Rangées du haut vers le bas, ancrées en bas.
        const grid = item.collisionGrid;
        const alpha = this.furnitureAlpha(sprite);
        grid.forEach((row, r) => {
          const y = bottom - (grid.length - r) * ts;
          for (let c = 0; c < row.length;) {
            if (row[c] !== '1') { c++; continue; }
            const start = c;
            while (row[c] === '1') c++;
            // Emprise réelle : pixels pleins au pied de l'objet dans ces cases
            // (le pot d'une plante, pas toute la case), sinon les cases entières.
            const fit = alpha && footprintInBand(alpha, start * ts, c * ts, sprite.height - (grid.length - r) * ts, ts);
            // (`fit` est relatif au début de la suite de cases.)
            // Case du dessus bloquante aussi (toit, façade) : le corps monte jusqu'en haut
            // de la case. Sinon il restait une fente de 8 px entre deux rangées, et Arcade,
            // qui résout un chevauchement de 1-2 px en poussant de côté, laissait l'avatar
            // s'y faufiler et traverser le bâtiment.
            const blockedAbove = r > 0 && grid[r - 1]!.slice(start, c).includes('1');
            if (fit) {
              const y0 = blockedAbove ? 0 : fit.y0;
              addBody(item.tileX * ts + start * ts + fit.x0, y + y0, fit.x1 - fit.x0, fit.y1 - y0);
            } else addBody(item.tileX * ts + start * ts, y, (c - start) * ts, ts);
          }
        });
      } else {
        addBody(item.tileX * ts, bottom - sprite.height, sprite.width, sprite.height);
      }
      this.furnitureBodies.set(item.id, bodies);
    }
    if (usable) {
      this.rememberSeat(item, sprite);
      const entry = findFurniture(item.furnitureId);
      const deskItem = item.id.startsWith('pd-item-');
      const kind = entry && isWhiteboard(entry.id) ? 'whiteboard' as const
        : entry && !deskItem && isNoticeBoard(entry.id) ? 'notice' as const : null;
      if (entry && kind) {
        const ts = this.TILE_SIZE;
        const footY = (item.tileY + 1) * ts;
        const boardId = deskItem ? item.id.slice('pd-item-'.length) : item.id;
        this.boards.set(item.id, { x: item.tileX * ts + entry.dimensions.w / 2, y: footY - entry.dimensions.h, footY, name: entry.label, boardId, kind });
      }
    }
    return { sprite, bodies };
  }

  /** Opacité des pixels d'un meuble affiché (en cache par texture et image), null si illisible. */
  private furnitureAlpha(sprite: Phaser.GameObjects.Image): AlphaMap | null {
    const frame = sprite.frame;
    const key = `${sprite.texture.key}:${frame.name}`;
    const cached = furnitureAlphaCache.get(key);
    if (cached !== undefined) return cached;
    let map: AlphaMap | null = null;
    try {
      const w = frame.cutWidth, h = frame.cutHeight;
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (ctx) {
        ctx.drawImage(frame.source.image as CanvasImageSource, frame.cutX, frame.cutY, w, h, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        map = { w, h, at: (x, y) => data[(y * w + x) * 4 + 3]! };
      }
    } catch { map = null; }
    furnitureAlphaCache.set(key, map);
    return map;
  }

  /** Siège (chaise, fauteuil, canapé : une place par coussin) : repéré pour l'assise. */
  private rememberSeat(item: { id: string; furnitureId: string; tileX: number; tileY: number }, sprite: Phaser.GameObjects.Image): void {
    const entry = findFurniture(item.furnitureId);
    if (!entry) return;
    const direction = seatDirection(item.furnitureId, entry.category);
    if (!direction) return;
    const ts = this.TILE_SIZE;
    const { w, h } = entry.dimensions;
    const left = item.tileX * ts, top = (item.tileY + 1) * ts - h;
    seatSpots(item.furnitureId, direction, w, h).forEach(({ dx, dy, lift }, i) => {
      this.seats.set(`${item.id}#${i}`, { x: left + dx, y: top + dy, direction, lift, sprite });
    });
  }

  /** Toutes les places d'un meuble (un canapé en a plusieurs). */
  private forgetSeat(itemId: string): void {
    this.boards.delete(itemId);
    for (const key of [...this.seats.keys()]) if (key.startsWith(`${itemId}#`)) this.seats.delete(key);
  }

  // ── Bureaux personnels (game/systems/PersonalDesks.ts) ─────

  private createPersonalDesks(): void {
    if (!this.zoneConfig) return;
    const desks = new PersonalDesks(this, {
      tile: this.TILE_SIZE,
      mapId: this.zoneConfig.slug,
      localUserId: this.localPlayerId,
      spawnItem: ({ depthOffset, usable, ...item }) => {
        const spawned = this.spawnFurnitureItem(item, usable);
        if (!spawned) return null;
        if (depthOffset) spawned.sprite.setDepth(spawned.sprite.depth + depthOffset);
        return {
          destroy: () => {
            spawned.sprite.destroy();
            for (const b of spawned.bodies) b.destroy();
            this.furnitureBodies.delete(item.id);
            this.forgetSeat(item.id);
          },
        };
      },
      // Collisions réelles (murs, eau, objets solides, portes fermées, fichier Tiled)
      // + mêmes obstacles que l'API : tout objet de l'admin sauf « au sol », toute porte.
      blockedCells: () => {
        const { grid } = this.buildWalkGrid();
        const ts = this.TILE_SIZE;
        const obstacles = deskObstacles({
          furniture: this.dbFurniture.map((f) => f.item),
          doors: this.doorZones.map((d) => ({
            x: Math.floor(d.bounds.x / ts), y: Math.floor(d.bounds.y / ts),
            width: Math.max(1, Math.round(d.bounds.width / ts)), height: Math.max(1, Math.round(d.bounds.height / ts)),
          })),
        });
        return (cx, cy) => isBlocked(grid, cx, cy) || obstacles(cx, cy);
      },
      onLayoutChanged: () => {
        this.ensureLocalPlayerOnFreeCell();
        this.updateDimmedArea();
      },
      onCurrentDeskChanged: () => this.updateDimmedArea(),
      playerPosition: () => (this.localPlayer ? { x: this.localPlayer.x, y: this.localPlayer.y } : null),
      remotePlayers: () => {
        const players = [...useGameStore.getState().zonePlayers.values()];
        const list: Array<{ userId: string; name: string; x: number; y: number }> = [];
        this.remotePlayerSprites.forEach((sprite, sessionId) => {
          const userId = this.sessionIdToUserId.get(sessionId);
          if (!userId) return;
          list.push({ userId, name: players.find((p) => p.userId === userId)?.displayName ?? 'Quelqu\u2019un', x: sprite.x, y: sprite.y });
        });
        return list;
      },
      inputBlocked: () => this.chatInputActive,
      focusCamera: (x, y) => {
        this.cameraDetached = true; // le suivi reprend dès que l'avatar bouge (handleInput)
        this.cameras.main.stopFollow();
        this.cameraFollowing = null;
        this.cameras.main.pan(x, y, 400, 'Sine.easeOut', true);
      },
      releaseCamera: () => {
        if (!this.localPlayer || !this.cameraDetached) return;
        this.cameraDetached = false;
        this.followCameraTarget(true); // glissé vers l'avatar, ou la salle cadrée
      },
    });
    this.personalDesks = desks;

    // Pose, aménagement, libération : rechargement regroupé.
    let reloadTimer: ReturnType<typeof setTimeout> | null = null;
    this.listen('colyseus:desks-changed', () => {
      if (reloadTimer) return;
      // Chaque bureau est aussi une bulle (voile autour du bureau où l'on se tient).
      reloadTimer = setTimeout(() => { reloadTimer = null; void desks.load(); this.loadBubbleBounds(); }, 300);
    });
    this.sceneCleanups.push(() => {
      if (reloadTimer) clearTimeout(reloadTimer);
      desks.destroy();
      if (this.personalDesks === desks) this.personalDesks = null;
    });
  }

  // ── Murs créés via l'éditeur (map_walls) ───────────────────
  // Rendu : WallRenderer (assemblage automatique selon les voisins, voir
  // computeWallTiles dans @campus/core). Collision : chaque cellule peinte
  // est un bloc solide — la face qui déborde sur la case du dessus ne bloque
  // pas (l'avatar venant du nord passe derrière, comme sur les murs Tiled).

  private loadLabelsFromDB(): void {
    if (!this.zoneConfig) return;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(this.zoneConfig.slug)}/labels`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': getTenantSlug(),
      },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((labels: MapLabel[]) => {
        if (!this.sys.isActive()) return;
        this.labelRenderer ??= new LabelRenderer(this);
        this.labelRenderer.render(labels);
      })
      .catch((err) => console.warn('[ZoneScene] load labels failed:', err));
  }

  private loadFloorsFromDB(): void {
    if (!this.zoneConfig) return;
    const mapId = this.zoneConfig.slug;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/floors`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': getTenantSlug(),
      },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((layers: Array<FloorLayerData & { layer: FloorLayerName }>) => {
        if (!this.sys.isActive()) return;
        this.floorRenderer ??= new FloorRenderer(this, { floor: FLOOR_DEPTHS.floor, overlay: FLOOR_DEPTHS.overlay });
        this.floorData.clear();
        for (const l of layers) {
          this.floorRenderer.render(l.layer, l);
          this.floorData.set(l.layer, l);
        }
        this.rebuildWaterBodies();
      })
      .catch((err) => console.warn('[ZoneScene] load floors failed:', err));
  }

  /**
   * Collision de l'eau peinte : une case bloque si isFloorCellBlocking() le
   * dit et qu'aucun objet « au sol » (pont, ponton) ne la couvre. Cases
   * contiguës d'une même rangée fusionnées en un seul corps (un étang = une
   * poignée de corps, pas un par case). Rappelé à chaque rechargement des
   * sols ou du mobilier — tout est reconstruit.
   */
  private rebuildWaterBodies(): void {
    for (const b of this.waterBodies) b.destroy();
    this.waterBodies = [];
    if (!this.localPlayer) return;
    const floor = this.floorData.get('floor');
    const overlay = this.floorData.get('overlay');
    if (!floor && !overlay) return;

    const width = Math.max(floor?.width ?? 0, overlay?.width ?? 0);
    const height = Math.max(floor?.height ?? 0, overlay?.height ?? 0);
    const ts = this.TILE_SIZE;
    const blocked = (x: number, y: number) =>
      isFloorCellBlocking(floor, overlay, x, y) && !this.floorObjectCells.has(y * MAX_FLOOR_DIMENSION + x);

    for (let y = 0; y < height; y++) {
      let x = 0;
      while (x < width) {
        if (!blocked(x, y)) { x++; continue; }
        const start = x;
        while (x < width && blocked(x, y)) x++;
        const run = x - start;
        const body = this.physics.add.staticImage(start * ts + (run * ts) / 2, y * ts + ts / 2, '__DEFAULT');
        body.setDisplaySize(run * ts, ts);
        body.setVisible(false);
        body.refreshBody();
        this.physics.add.collider(this.localPlayer, body);
        this.waterBodies.push(body);
      }
    }
    this.ensureLocalPlayerOnFreeCell();
  }

  private loadWallsFromDB(): void {
    if (!this.zoneConfig) return;
    const mapId = this.zoneConfig.slug;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    const tenantSlug = getTenantSlug();

    interface WallItem {
      id: string;
      tileX: number;
      tileY: number;
      wallStyleId: string;
    }

    fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/walls`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': tenantSlug,
      },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((items: WallItem[]) => {
        // Rejouable (voir EventBus 'colyseus:walls-changed') : on remplace
        // tout l'état de la charge précédente.
        for (const b of this.wallBodies.values()) b.destroy();
        this.wallBodies.clear();

        this.wallRenderer ??= new WallRenderer(this, (bottomY) => DEPTH_Y_BASE + bottomY / DEPTH_Y_SCALE);
        this.wallRenderer.render(items);
        for (const item of items) this.spawnWallBody(item);
        this.ensureLocalPlayerOnFreeCell();
        this.replanWalkRoute();
        console.log(`[ZoneScene] loaded ${items.length} walls from DB`);
      })
      .catch((err) => console.warn('[ZoneScene] load walls failed:', err));
  }

  private spawnWallBody(item: { id: string; tileX: number; tileY: number }): void {
    if (!this.localPlayer) return;
    const ts = this.TILE_SIZE;
    const body = this.physics.add.staticImage(item.tileX * ts + ts / 2, item.tileY * ts + ts / 2, '__DEFAULT');
    body.setDisplaySize(ts, ts);
    body.setVisible(false);
    body.refreshBody();
    this.physics.add.collider(this.localPlayer, body);
    this.wallBodies.set(item.id, body);
  }

  override update(time: number, delta: number) {
    this.handleInput(delta);
    this.followPerson();
    this.sortDepths();
    this.drawProximityBubble();
    this.emitOverlayPositions();
    this.checkPortals();
    if (this.localPlayer && !this.portalFading && !this.elevatorWalking) this.elevators?.update(this.localPlayer.x, this.localPlayer.y);
    this.checkDoors();
    this.checkScooters();
    this.checkDoorAction();
    this.checkAreas();
    this.checkDesks();
    this.personalDesks?.update(time);
    this.checkPodium();
    this.drawPodiumSpotlight();
    this.updateSpeakerPingPositions();
  }

  /**
   * Depth sorting dynamique par Y — chaque frame.
   * Les avatars dont le Y est plus petit (plus haut sur la map) ont un depth
   * plus faible, donc passent derrière les meubles "abovePlayer" dont le tile
   * a un depth calculé de la même façon.
   */
  private sortDepths(): void {
    if (this.localPlayer) {
      this.localPlayer.setDepth(this.localDepthOverride ?? this.avatarDepth(this.localPlayer));
    }
    this.remotePlayerSprites.forEach((sprite, sessionId) => {
      sprite.setDepth(this.remoteDepthOverrides.get(sessionId) ?? this.avatarDepth(sprite));
    });
  }

  /**
   * Assis, l'avatar a le même bas que son siège : il passe juste devant lui
   * (siège vu de face ou de profil), ou juste derrière quand on est assis de
   * dos, pour que le dossier cache le bas du dos.
   */
  private avatarDepth(sprite: CompositeAvatar): number {
    const depth = DEPTH_Y_BASE + sprite.y / DEPTH_Y_SCALE;
    if (!sprite.isSeated) return depth;
    // Profondeur réelle du siège, pas celle de sa position : sur un bureau
    // personnel, un siège derrière une table est descendu d'un cran
    // (deskItemDepthOffsets) — calé sur sa position, l'avatar repassait devant
    // le dossier.
    let seatDepth = depth;
    for (const seat of this.seats.values()) {
      if (sameSeatSpot(seat, sprite)) { seatDepth = seat.sprite.depth; break; }
    }
    return seatDepth + (sprite.seatedFacingUp ? -0.01 : 0.01);
  }

  /** Verifie si Space ou E vient d'etre presse pour ouvrir une porte. */
  private checkDoorAction(): void {
    if (this.chatInputActive) return;
    if (!this.nearbyDoorId && !this.scooterPromptMode) return;

    const spaceJust = this.keySpace && Phaser.Input.Keyboard.JustDown(this.keySpace);
    const eJust = this.keyE && Phaser.Input.Keyboard.JustDown(this.keyE);

    if (spaceJust || eJust) this.triggerAction();
  }

  /** Touche d'action (Espace/E, invite tactile) : la porte à portée d'abord, sinon la trottinette. */
  private triggerAction(): void {
    if (this.nearbyDoorId) this.triggerDoorAction();
    else this.triggerScooterAction();
  }

  /**
   * Ouvre (ou déclenche le knock si verrouillée) la porte proche du joueur local.
   * Appelée par le clavier (Space/E, via checkDoorAction) et par le bouton d'action tactile
   * (event input:action-press).
   */
  private triggerDoorAction(): void {
    if (this.chatInputActive) return;
    if (!this.nearbyDoorId) return;

    if (this.nearbyDoorId.startsWith('lock:')) {
      const bubbleId = this.nearbyDoorId.slice('lock:'.length);
      EventBus.emit('ui:toast', { message: 'Ce salon est fermé.', tone: 'warning' });
      EventBus.emit('ui:knock-prompt', { bubbleId });
      return;
    }
    const door = this.doorZones.find(d => d.id === this.nearbyDoorId);
    if (!door) return;

    // Comparaison sur l'id résolu (Fix I-2 / Task 19), jamais sur
    // linkedBubble (nom en clair).
    const linked = door.resolvedBubbleId;

    if (linked && this.isRoomReservedForLocalPlayer(linked)) {
      this.doorSprites.get(door.id)?.playLocked();
      const message = this.isRoomClaimedByOthers(linked)
        ? 'Salle revendiquée par un duo jusqu\'à dimanche.'
        : 'Réservé : tu n\'as pas accès à cette salle.';
      EventBus.emit('ui:toast', { message, tone: 'warning' });
      return;
    }

    if (this.isLocalPlayerBubbleOwner(linked)) {
      // Owner garde toujours l'accès
      this.allowedBubbleIds.add(linked);
    }

    if (this.isDoorLockedForLocalPlayer(door)) {
      this.doorSprites.get(door.id)?.playLocked();
      EventBus.emit('ui:toast', { message: 'Cette salle est fermée.', tone: 'warning' });
      EventBus.emit('ui:knock-prompt', { bubbleId: linked });
      return;
    }

    this.doorSprites.get(door.id)?.open();
    this.toggleDoorCollision(door);
  }

  // ── Methodes publiques (appelees par ColyseusClient) ──────

  /** Position actuelle de l'avatar local (reconnexion temps réel : on revient au même endroit). */
  getLocalPlayerPosition(): { x: number; y: number } | null {
    return this.localPlayer ? { x: this.localPlayer.x, y: this.localPlayer.y } : null;
  }

  /**
   * Retire tous les avatars distants — connexion temps réel perdue : ils ne
   * bougeraient plus. La reconnexion les recrée (room:state, nouveaux sessionId).
   */
  /** Avatar distant sous ce point du monde (le plus au premier plan), sinon null. */
  private remoteAvatarAt(x: number, y: number): string | null {
    let found: string | null = null;
    let foundY = -Infinity;
    for (const [sessionId, sprite] of this.remotePlayerSprites) {
      if (sprite.y > foundY && sprite.containsWorldPoint(x, y)) {
        found = sessionId;
        foundY = sprite.y;
      }
    }
    return found;
  }

  removeAllRemotePlayers(): void {
    for (const sessionId of [...this.remotePlayerSprites.keys()]) this.removeRemotePlayer(sessionId);
  }

  // ── Trottinettes (appelées par ColyseusClient) ──

  setScooterSnapshots(list: ScooterSnapshot[]): void {
    if (this.scooters) this.scooters.setState(list);
    else this.pendingScooterState = list;
  }

  updateScooter(s: ScooterSnapshot): void {
    if (this.scooters) this.scooters.update(s);
    else this.pendingScooterState = [...(this.pendingScooterState ?? []).filter((p) => p.itemId !== s.itemId), s];
  }

  /** Le serveur confirme que le joueur local monte ('' = descend). */
  setLocalVehicle(vehicle: string): void {
    const model = isScooterModel(vehicle) ? vehicle : null;
    if (model === this.localVehicle) return;
    this.localVehicle = model;
    this.localPlayer?.setVehicle(model);
  }

  /** Le serveur confirme l'assise du joueur local ('' = debout). */
  setLocalSeat(value: string): void {
    if (value === this.localSeatValue) return;
    const seat = parseSeat(value);
    const wasSeated = this.localSeat;
    this.localSeatValue = seat ? value : '';
    // Même siège : seule la frappe au clavier a changé.
    if (seat && wasSeated && sameSeatSpot(seat, wasSeated)) {
      this.localSeat = seat;
      this.localPlayer?.setSeat({ direction: seat.direction, typing: seat.typing, lift: seat.lift });
      return;
    }
    if (seat) this.sitLocal(seat);
    else this.standLocal(false);
  }

  /** Place l'avatar sur le siège : corps physique coupé (le siège est souvent solide), pose assise. */
  private sitLocal(seat: Seat): void {
    if (!this.localPlayer) return;
    this.localSeat = seat;
    this.clearMoveTarget();
    const body = this.localPlayer.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(0, 0);
    this.localPlayer.setPosition(seat.x, seat.y);
    body.reset(seat.x, seat.y);
    body.enable = false;
    this.lastDirection = seat.direction;
    this.lastEmittedAnimation = 'idle';
    this.localPlayer.setSeat({ direction: seat.direction, typing: seat.typing, lift: seat.lift });
    EventBus.emit('player:moved', { x: seat.x, y: seat.y, direction: seat.direction, animation: 'idle' });
  }

  /**
   * Se lève en reculant dans le dos du siège (à gauche d'une chaise tournée
   * vers la droite, sous une chaise de dos…) : devant, il y a souvent une table.
   * Case prise : case libre la plus proche. `announce` : prévenir le serveur.
   */
  private standLocal(announce: boolean): void {
    const seat = this.localSeat;
    if (!seat) return;
    this.localSeat = null;
    this.localSeatValue = '';
    if (announce) EventBus.emit('seat:stand-request', {});
    if (!this.localPlayer) return;
    const body = this.localPlayer.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.reset(this.localPlayer.x, this.localPlayer.y);
    this.localPlayer.setSeat(null);
    const { grid } = this.buildWalkGrid();
    const ts = grid.tileSize;
    // Centre du corps au centre de la case, d'après l'écart ancre/centre relevé
    // debout : le corps était coupé pendant l'assise, et après un placement son
    // centre n'est recalculé qu'à l'image suivante.
    const at = (cx: number, cy: number) => ({ x: (cx + 0.5) * ts + this.bodyAnchorOffset.x, y: (cy + 0.5) * ts + this.bodyAnchorOffset.y });
    // Se lever ne fait jamais sortir de la bulle où l'on est assis : sinon la
    // salle de réunion se quitte et se rejoint (son coupé). Vu sur Copyhouse le
    // 2026-10-29 : les chaises du haut de la salle touchent le bord de la bulle,
    // la case derrière elles est hors de la salle. Ordre : derrière, côtés, devant.
    const bubbleId = useGameStore.getState().localBubbleId;
    const room = bubbleId ? this.bubbleBounds.get(bubbleId) : undefined;
    const inRoom = (p: { x: number; y: number }) => !room
      || (p.x >= room.x && p.x <= room.x + room.width && p.y >= room.y && p.y <= room.y + room.height);
    const [bx, by] = STAND_BACK[seat.direction];
    const sx = Math.floor(seat.x / ts), sy = Math.floor((seat.y - 1) / ts);
    const steps: Array<[number, number]> = [[bx, by], [by, bx], [-by, -bx], [-bx, -by]];
    const cell = steps.map(([dx, dy]) => [sx + dx, sy + dy] as const)
      .find(([cx, cy]) => !isBlocked(grid, cx, cy) && inRoom(at(cx, cy)));
    if (!cell) {
      this.ensureLocalPlayerOnFreeCell();
      return;
    }
    const { x, y } = at(cell[0], cell[1]);
    this.localPlayer.setPosition(x, y);
    body.reset(x, y);
    EventBus.emit('player:moved', { x, y, direction: this.lastDirection, animation: 'idle' });
  }

  syncLocalPlayerSpawn(x: number, y: number): void {
    if (!this.localPlayer || this.elevatorWalking) return;
    this.localPlayer.setPosition(x, y);
    console.log(`[ZoneScene] syncLocalPlayerSpawn → (${x}, ${y})`);
  }

  upsertRemotePlayer(state: PlayerStateDTO): void {
    // L'ascenseur le fait entrer ou sortir : on garde sa position, rendue à la fin.
    const script = this.remoteElevatorScripts.get(state.sessionId);
    if (script && this.remotePlayerSprites.has(state.sessionId)) { script.latest = state; return; }
    const isNew = !this.remotePlayerSprites.has(state.sessionId);
    console.log(`[ZoneScene] upsertRemotePlayer sessionId=${state.sessionId} userId=${state.userId} isNew=${isNew} x=${state.x} y=${state.y}`);

    // Map Colyseus animation+direction → CompositeAvatar anim key (calculé tôt : utilisé
    // aussi dans le callback async de chargement d'apparence ci-dessous).
    const compositeAnimKey = state.animation === 'walk' ? `walk_${state.direction}` : `idle_${state.direction}`;

    let sprite = this.remotePlayerSprites.get(state.sessionId);
    if (!sprite) {
      sprite = new CompositeAvatar(this, state.x, state.y, state.displayName);
      // Depth set dynamically in sortDepths()
      let remoteAppearance: AvatarAppearance = DEFAULT_APPEARANCE;
      if (state.appearanceJson) {
        try { remoteAppearance = JSON.parse(state.appearanceJson); } catch { /* use default */ }
      }
      loadAppearanceIfNeeded(this, remoteAppearance, () => {
        sprite!.updateAppearance(remoteAppearance);
        // Un joueur immobile n'émet plus jamais de player:moved après son join
        // (voir handleInput — throttle sur `moving`), donc c'est ICI, et pas au
        // prochain mouvement, qu'il faut rejouer l'anim avec les layers fraîchement
        // chargés — sinon le sprite reste invisible jusqu'à ce que quelqu'un bouge.
        sprite!.playCompositeAnimation(compositeAnimKey);
      });
      this.remotePlayerSprites.set(state.sessionId, sprite);
      this.sessionIdToUserId.set(state.sessionId, state.userId);

    } else {
      sprite.setDisplayName(state.displayName);
    }

    sprite.setPosition(state.x, state.y);
    sprite.setStatus(state.status ?? 'available');
    sprite.setVehicle(isScooterModel(state.vehicle) ? state.vehicle : null);
    const seat = parseSeat(state.seat);
    sprite.setSeat(seat ? { direction: seat.direction, typing: seat.typing, lift: seat.lift } : null);
    if (seat) this.remoteSeats.set(state.sessionId, state.seat!);
    else this.remoteSeats.delete(state.sessionId);

    if (sprite.getCurrentCompositeAnim() !== compositeAnimKey) {
      sprite.playCompositeAnimation(compositeAnimKey);
    }
  }

  removeRemotePlayer(sessionId: string): void {
    const userId = this.sessionIdToUserId.get(sessionId);
    if (userId) {
      const ping = this.speakerPings.get(userId);
      if (ping) {
        this.tweens.killTweensOf(ping);
        ping.destroy();
        this.speakerPings.delete(userId);
      }
    }
    const sprite = this.remotePlayerSprites.get(sessionId);
    this.remoteElevatorScripts.get(sessionId)?.end.remove();
    this.remoteElevatorScripts.delete(sessionId);
    this.remoteDepthOverrides.delete(sessionId);
    if (sprite) this.tweens.killTweensOf(sprite);
    sprite?.destroy();
    this.remotePlayerSprites.delete(sessionId);
    this.remoteSeats.delete(sessionId);
    this.sessionIdToUserId.delete(sessionId);
  }

  // ── Methodes privees ──────────────────────────────────────

  /**
   * Deplace le joueur local via Arcade Physics velocity.
   * Les collisions sont gerees automatiquement par le physics engine.
   */
  private handleInput(delta: number): void {
    if (!this.localPlayer || !this.cursors) return;
    if (this.chatInputActive) {
      // Clavier rendu à une fenêtre ou à un champ de texte : l'avatar s'arrête
      // (avant, il gardait sa dernière vitesse et filait jusqu'au premier mur).
      const b = this.localPlayer.body as Phaser.Physics.Arcade.Body | null;
      if (b?.enable) b.setVelocity(0, 0);
      return;
    }

    const body = this.localPlayer.body as Phaser.Physics.Arcade.Body;
    if (this.portalFading || this.elevatorWalking) { if (body.enable) body.setVelocity(0, 0); return; }
    let vx = 0;
    let vy = 0;
    this.localPlayer.anims.timeScale = 1;

    const held = this.heldMoveCodes;
    const left  = held.has('KeyA') || held.has('ArrowLeft');
    const right = held.has('KeyD') || held.has('ArrowRight');
    const up    = held.has('KeyW') || held.has('ArrowUp');
    const down  = held.has('KeyS') || held.has('ArrowDown');
    const keyboardActive = left || right || up || down;

    if (!this.localSeat && body.enable) {
      // Juste après un placement, le centre n'est pas encore recalculé : écart aberrant ignoré.
      const ox = this.localPlayer.x - body.center.x, oy = this.localPlayer.y - body.center.y;
      if (Math.abs(ox) < 8 && oy > 0 && oy < 24) this.bodyAnchorOffset = { x: ox, y: oy };
    }
    // Assis : immobile ; une touche de déplacement (ou un tap-to-move) fait se lever.
    if (this.localSeat) {
      if (!keyboardActive && !this.moveTarget) return;
      this.standLocal(true);
    }

    if (keyboardActive) {
      // Le clavier reprend toujours la main sur une destination tap-to-move en cours.
      this.clearMoveTarget();
      walkRoute = null;
      this.walkRoutePortal = null;

      // Sur la trottinette : vitesse fixe, pas de sprint.
      const sprinting = !this.localVehicle && !!this.keyShift?.isDown;
      const speed = this.localVehicle ? PLAYER_SPEED * SCOOTER_SPEED_MULTIPLIER
        : sprinting ? PLAYER_SPEED * SPRINT_MULTIPLIER : PLAYER_SPEED;

      if (left)  { vx = -speed; this.lastDirection = 'left'; }
      if (right) { vx =  speed; this.lastDirection = 'right'; }
      if (up)    { vy = -speed; this.lastDirection = 'up'; }
      if (down)  { vy =  speed; this.lastDirection = 'down'; }

      // Normalisation diagonale (clavier uniquement — digital)
      if (vx !== 0 && vy !== 0) {
        const inv = 1 / Math.SQRT2;
        vx *= inv;
        vy *= inv;
      }

      // Anime la marche plus vite pendant le sprint (évite l'effet "glisse").
      if (sprinting && (vx !== 0 || vy !== 0)) {
        this.localPlayer.anims.timeScale = SPRINT_MULTIPLIER;
      }
    } else if (this.moveTarget) {
      // Tap-to-move — suit l'itinéraire calculé (setMoveTarget) étape par étape,
      // guidé par le centre du corps physique (les pieds, 4 px au-dessus de l'ancre).
      let dx = this.moveTarget.x - body.center.x;
      let dy = this.moveTarget.y - body.center.y;
      let dist = Math.hypot(dx, dy);
      if (dist <= this.ARRIVE_THRESHOLD_PX && this.movePath.length > 1) {
        this.movePath.shift();
        this.moveTarget = this.movePath[0]!;
        dx = this.moveTarget.x - body.center.x;
        dy = this.moveTarget.y - body.center.y;
        dist = Math.hypot(dx, dy);
      }
      this.autoOpenDoorOnPath();

      if (dist <= this.ARRIVE_THRESHOLD_PX) {
        this.clearMoveTarget();
        this.onTapMoveEnded();
      } else if (this.isMoveStuck(delta)) {
        // Bloqué (porte qui se referme, autre obstacle) : on recalcule une
        // fois depuis la position actuelle, puis on abandonne.
        const final = this.moveFinalTarget;
        const retry = !this.moveReplanned && final;
        this.clearMoveTarget();
        if (retry) { this.setMoveTarget(final.x, final.y); this.moveReplanned = true; }
        else this.onTapMoveEnded();
      } else {
        const speed = this.localVehicle ? PLAYER_SPEED * SCOOTER_SPEED_MULTIPLIER : PLAYER_SPEED;
        vx = (dx / dist) * speed;
        vy = (dy / dist) * speed;
        this.lastDirection = Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down');
      }
    }

    if (keyboardActive) [vx, vy] = this.slideAroundCorner(body, vx, vy);
    body.setVelocity(vx, vy);

    const moving = vx !== 0 || vy !== 0;

    // La caméra a été détachée par un pan manuel — se recentre dès que le joueur rebouge.
    // Vrai glissé (tween avec easing) depuis la position actuelle de la caméra vers
    // l'avatar, plutôt qu'un lerp exponentiel (qui donnait un rattrapage encore brusque
    // sur une grande distance) — le suivi normal reprend une fois le glissé terminé.
    if (moving && this.cameraDetached) {
      this.cameraDetached = false;
      this.followCameraTarget(true); // glissé vers l'avatar, ou la salle cadrée
    }

    // Animation CompositeAvatar
    const newAnimation: 'idle' | 'walk' = moving ? 'walk' : 'idle';
    const compositeAnimKey = moving ? `walk_${this.lastDirection}` : `idle_${this.lastDirection}`;
    if (this.localPlayer.getCurrentCompositeAnim() !== compositeAnimKey) {
      this.localPlayer.playCompositeAnimation(compositeAnimKey);
    }

    // Emission vers Colyseus (throttle)
    const now = Date.now();
    const animChanged = newAnimation !== this.lastEmittedAnimation;
    const throttleOk = now - this.lastMoveEmitTime >= this.MOVE_EMIT_INTERVAL_MS;

    if (animChanged || (moving && throttleOk)) {
      this.lastMoveEmitTime = now;
      this.lastEmittedAnimation = newAnimation;
      EventBus.emit('player:moved', {
        x:         this.localPlayer.x,
        y:         this.localPlayer.y,
        direction: this.lastDirection,
        animation: newAnimation,
      });
    }
  }

  /**
   * Fixe la destination tap-to-move : calcule un itinéraire qui contourne les
   * obstacles (Pathfinder) et dessine le marqueur. Destination dans un
   * obstacle → case libre la plus proche ; inatteignable → au plus près.
   */
  private setMoveTarget(x: number, y: number): boolean {
    if (!this.localPlayer) return false;
    this.clearMoveTarget();
    const grid = this.buildWalkGrid();
    const playerBody = this.localPlayer.body as Phaser.Physics.Arcade.Body | null;
    const from = playerBody ? { x: playerBody.center.x, y: playerBody.center.y } : { x: this.localPlayer.x, y: this.localPlayer.y };
    // Demi-largeur réelle du corps (14 px × échelle 2 de l'ancre = 28 px).
    const halfWidth = playerBody?.halfWidth ?? 14;
    const path = findPath(grid.grid, from, { x, y }, halfWidth + 1);
    if (!path || path.length === 0) return false;

    this.movePath = path;
    this.moveTarget = path[0]!;
    this.moveFinalTarget = { x, y };
    this.moveReplanned = false;
    this.moveStuckSince = 0;
    this.moveLastPos = from;
    // Portes traversées par l'itinéraire (échantillonné tous les 8 px).
    let prev = from;
    for (const p of path) {
      const steps = Math.max(1, Math.ceil(Math.hypot(p.x - prev.x, p.y - prev.y) / 8));
      for (let k = 0; k <= steps; k++) {
        const c = cellOf(grid.grid, { x: prev.x + ((p.x - prev.x) * k) / steps, y: prev.y + ((p.y - prev.y) * k) / steps });
        const doorId = grid.doorCells.get(c.y * grid.grid.width + c.x);
        if (doorId) this.movePathDoorIds.add(doorId);
      }
      prev = p;
    }

    const end = path[path.length - 1]!;
    if (!this.moveTargetMarker) return true;
    this.moveTargetMarker.clear();
    this.moveTargetMarker.lineStyle(2, 0xffffff, 0.85);
    this.moveTargetMarker.strokeCircle(end.x, end.y, 10);
    this.moveTargetMarker.lineStyle(2, 0xffffff, 0.5);
    this.moveTargetMarker.strokeCircle(end.x, end.y, 16);
    return true;
  }

  /** Efface la destination tap-to-move et son marqueur (arrivée ou reprise clavier). */
  private clearMoveTarget(): void {
    this.moveTarget = null;
    this.movePath = [];
    this.moveFinalTarget = null;
    this.movePathDoorIds.clear();
    this.moveTargetMarker?.clear();
  }

  /** Ouvre la porte à portée si elle est sur l'itinéraire (une fois par porte). */
  private autoOpenDoorOnPath(): void {
    const id = this.nearbyDoorId;
    if (!id || !this.movePathDoorIds.has(id)) return;
    this.movePathDoorIds.delete(id);
    this.triggerDoorAction();
  }

  /** Vrai si l'avatar n'a quasiment pas bougé depuis 600 ms alors qu'il devrait marcher. */
  private isMoveStuck(delta: number): boolean {
    if (!this.localPlayer) return false;
    const pos = { x: this.localPlayer.x, y: this.localPlayer.y };
    const last = this.moveLastPos;
    this.moveLastPos = pos;
    if (last && Math.hypot(pos.x - last.x, pos.y - last.y) > 0.5) {
      this.moveStuckSince = 0;
      return false;
    }
    this.moveStuckSince += delta;
    return this.moveStuckSince > 600;
  }

  /**
   * Grille des cases bloquantes, construite à partir des collisions réelles :
   * tuiles `collides` des calques TMJ et tous les corps statiques actifs
   * (murs, eau, mobilier solide, portes fermées). Les portes non verrouillées
   * sont praticables (ouvertes automatiquement en chemin), les verrouillées non.
   */
  /**
   * Glissement dans les coins (clavier, un seul axe) : si l'avatar bute sur un
   * obstacle qui ne recouvre qu'un bord de ses pieds, sur `CORNER_SLIDE_PX` au
   * plus, il glisse de côté vers le passage au lieu de rester coincé contre le
   * chambranle d'une porte ou le coin d'un meuble.
   */
  private slideAroundCorner(body: Phaser.Physics.Arcade.Body, vx: number, vy: number): [number, number] {
    if ((vx !== 0) === (vy !== 0)) return [vx, vy];
    const vertical = vy !== 0;
    const blocked = vertical
      ? (vy < 0 ? body.blocked.up || body.touching.up : body.blocked.down || body.touching.down)
      : (vx < 0 ? body.blocked.left || body.touching.left : body.blocked.right || body.touching.right);
    if (!blocked) return [vx, vy];
    // Bande de 2 px devant les pieds, dans le sens de la marche.
    const probe = vertical
      ? { x0: body.left, x1: body.right, y0: vy < 0 ? body.top - 2 : body.bottom, y1: vy < 0 ? body.top : body.bottom + 2 }
      : { x0: vx < 0 ? body.left - 2 : body.right, x1: vx < 0 ? body.left : body.right + 2, y0: body.top, y1: body.bottom };
    let lo = Infinity, hi = -Infinity;
    for (const other of this.physics.world.staticBodies.getArray()) {
      if (!other.enable || other.right <= probe.x0 || other.x >= probe.x1 || other.bottom <= probe.y0 || other.y >= probe.y1) continue;
      lo = Math.min(lo, vertical ? other.x : other.y);
      hi = Math.max(hi, vertical ? other.right : other.bottom);
    }
    if (lo > hi) return [vx, vy];
    const start = vertical ? body.left : body.top, end = vertical ? body.right : body.bottom;
    const speed = Math.abs(vertical ? vy : vx);
    let side = 0;
    if (lo <= start && hi < end && hi - start <= CORNER_SLIDE_PX) side = 1;        // obstacle sur le bord gauche (haut) : glisser à droite (bas)
    else if (hi >= end && lo > start && end - lo <= CORNER_SLIDE_PX) side = -1;  // obstacle sur le bord droit (bas) : glisser à gauche (haut)
    if (!side) return [vx, vy];
    return vertical ? [side * speed, vy] : [vx, side * speed];
  }

  private buildWalkGrid(): { grid: WalkGrid; doorCells: Map<number, string> } {
    const ts = this.TILE_SIZE;
    const width = Math.max(1, Math.ceil(this.mapWidth / ts));
    const height = Math.max(1, Math.ceil(this.mapHeight / ts));
    const blocked = new Uint8Array(width * height);
    const mark = (cx: number, cy: number, v: 0 | 1) => {
      if (cx >= 0 && cy >= 0 && cx < width && cy < height) blocked[cy * width + cx] = v;
    };

    for (const layer of this.collisionLayers) {
      layer.forEachTile((t) => { if (t.collides) mark(t.x, t.y, 1); });
    }

    // Un corps bloque une case s'il la recouvre d'au moins MIN_OVERLAP px dans
    // les deux axes (les meubles dont la largeur n'est pas un multiple de 32).
    const MIN_OVERLAP = 4;
    for (const body of this.physics.world.staticBodies.getArray()) {
      if (!body.enable) continue;
      const x0 = Math.floor(body.x / ts);
      const y0 = Math.floor(body.y / ts);
      const x1 = Math.floor((body.right - 0.01) / ts);
      const y1 = Math.floor((body.bottom - 0.01) / ts);
      for (let cy = y0; cy <= y1; cy++) {
        for (let cx = x0; cx <= x1; cx++) {
          const ox = Math.min(body.right, (cx + 1) * ts) - Math.max(body.x, cx * ts);
          const oy = Math.min(body.bottom, (cy + 1) * ts) - Math.max(body.y, cy * ts);
          if (ox >= MIN_OVERLAP && oy >= MIN_OVERLAP) mark(cx, cy, 1);
        }
      }
    }

    const doorCells = new Map<number, string>();
    for (const door of this.doorZones) {
      if (this.isDoorLockedForLocalPlayer(door)) continue;
      const { x, y, width: w, height: h } = door.bounds;
      for (let cy = Math.floor(y / ts); cy < Math.ceil((y + h) / ts); cy++) {
        for (let cx = Math.floor(x / ts); cx < Math.ceil((x + w) / ts); cx++) {
          if (cx < 0 || cy < 0 || cx >= width || cy >= height) continue;
          mark(cx, cy, 0);
          doorCells.set(cy * width + cx, door.id);
        }
      }
    }

    return { grid: { width, height, tileSize: ts, blocked }, doorCells };
  }

  /**
   * Si l'avatar local se retrouve dans une case bloquante (point d'apparition
   * sur de l'eau ou sous un meuble — ex. carte vierge, apparition au centre),
   * on le déplace sur la case libre la plus proche. Appelé après chaque
   * chargement de sols, murs et mobilier.
   *
   * Jugé sur le corps physique réel, jamais sur l'ancre : l'ancre est au
   * bord bas du corps, donc un avatar collé au-dessus d'un mur avait son
   * ancre pile sur la rangée du mur et se faisait téléporter de l'autre côté
   * à chaque rechargement (mobilier, murs, bureaux…).
   */
  /**
   * Vrai si le corps chevauche réellement un obstacle (corps statique actif ou
   * tuile `collides` du TMJ) de plus de 2 px — un simple contact, ou le léger
   * recouvrement laissé par la séparation d'Arcade, ne compte pas.
   */
  private bodyOverlapsObstacle(body: Phaser.Physics.Arcade.Body): boolean {
    const TOLERANCE = 2;
    const left = body.x + TOLERANCE;
    const top = body.y + TOLERANCE;
    const right = body.right - TOLERANCE;
    const bottom = body.bottom - TOLERANCE;
    for (const other of this.physics.world.staticBodies.getArray()) {
      if (!other.enable) continue;
      if (other.x < right && other.right > left && other.y < bottom && other.bottom > top) return true;
    }
    for (const layer of this.collisionLayers) {
      const tiles = layer.getTilesWithinWorldXY(left, top, right - left, bottom - top);
      if (tiles.some((t) => t.collides)) return true;
    }
    return false;
  }

  private ensureLocalPlayerOnFreeCell(): void {
    if (!this.localPlayer || this.elevatorWalking) return;
    const body = this.localPlayer.body as Phaser.Physics.Arcade.Body | null;
    if (!body || !this.bodyOverlapsObstacle(body)) return;
    const { grid } = this.buildWalkGrid();
    const free = nearestFreeCell(grid, { x: body.center.x, y: body.center.y });
    if (!free) return;
    // Centre du corps au centre de la case (l'ancre est décalée du centre).
    const x = (free.x + 0.5) * grid.tileSize + (this.localPlayer.x - body.center.x);
    const y = (free.y + 0.5) * grid.tileSize + (this.localPlayer.y - body.center.y);
    this.localPlayer.setPosition(x, y);
    body.reset(x, y);
    this.clearMoveTarget();
    EventBus.emit('player:moved', { x, y, direction: this.lastDirection, animation: 'idle' });
    console.log(`[ZoneScene] avatar dans une case bloquante → déplacé en (${free.x}, ${free.y})`);
  }

  private drawProximityBubble(): void {
    if (!this.proximityGraphics || !this.localPlayer) return;
    this.proximityGraphics.clear();

    if (this.localBubbleActive) return;

    const RADIUS = 75;
    const x = this.localPlayer.x;
    const y = this.localPlayer.y;

    if (this.proximityNearbyCount > 0) {
      this.proximityGraphics.fillStyle(0x4a9eff, 0.10);
      this.proximityGraphics.fillCircle(x, y, RADIUS);
      this.proximityGraphics.lineStyle(1.5, 0x4a9eff, 0.55);
      this.proximityGraphics.strokeCircle(x, y, RADIUS);
    } else {
      this.proximityGraphics.lineStyle(1, 0xffffff, 0.18);
      this.proximityGraphics.strokeCircle(x, y, RADIUS);
    }
  }

  /**
   * Monde → écran (px canvas), inverse exact de la transformation de la
   * caméra principale (Camera.preRender : le zoom est centré sur l'origine de
   * la caméra, pas sur scrollX/scrollY).
   */
  private worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
    const cam = this.cameras.main;
    const originX = cam.width * cam.originX;
    const originY = cam.height * cam.originY;
    return {
      x: cam.x + originX + (worldX - cam.scrollX - originX) * cam.zoom,
      y: cam.y + originY + (worldY - cam.scrollY - originY) * cam.zoom,
    };
  }

  private emitOverlayPositions(): void {
    const positions = new Map<string, { x: number; y: number }>();

    if (this.localPlayer && this.localPlayerId) {
      positions.set(`self:${this.localPlayerId}`, this.worldToScreen(this.localPlayer.x, this.localPlayer.y - 32));
    }

    this.remotePlayerSprites.forEach((sprite, sessionId) => {
      const userId = this.sessionIdToUserId.get(sessionId);
      if (!userId) return;
      positions.set(userId, this.worldToScreen(sprite.x, sprite.y - 32));
    });

    // Rien n'a bougé à l'écran (joueurs et caméra immobiles) : pas d'émission.
    // Les consommateurs (PlayerNameplates, ChatBubbles) gardent la dernière Map.
    if (this.lastOverlayPositions && samePositions(this.lastOverlayPositions, positions)) return;
    this.lastOverlayPositions = positions;
    EventBus.emit('overlay:positions-updated', positions);
  }

  /**
   * Verifie si le joueur local est sur un portail de zone.
   * Hysteresis pour eviter le portal ping-pong.
   */
  private checkPortals(): void {
    if (this.transitionCooldown || this.portalFading || !this.zoneConfig || !this.localPlayer) return;

    const px = this.localPlayer.x;
    const py = this.localPlayer.y;

    for (const portal of this.zoneConfig.portals) {
      const { x, y, width, height } = portal.triggerBounds;

      // Portails en bord de map (~30px de haut) : pas d'hysteresis, simple Contains.
      // Portails larges au milieu de la map : hysteresis classique pour eviter le ping-pong.
      const minDim = Math.min(width, height);
      let inside: boolean;
      if (minDim <= 64) {
        // Petit portail (bord de map) — simple test de contenance
        inside = px >= x && px <= x + width && py >= y && py <= y + height;
      } else {
        const buf = portal.hysteresisBuffer ?? this.HYSTERESIS_BUFFER_PX;
        inside = px >= x + buf && px <= x + width - buf && py >= y + buf && py <= y + height - buf;
      }

      if (inside) {
        // Carte hors du profil d'accès (ex. sortie de Copy Camp pour un
        // Copycamper) : le portail ne mène nulle part, on le dit une fois.
        const targetSlug = portal.targetZoneId.slice(portal.targetZoneId.indexOf(':') + 1);
        if (!canEnterZoneHere(targetSlug)) {
          if (this.reservedPortalId !== portal.id) {
            this.reservedPortalId = portal.id;
            const message = isZoneClosedHere(targetSlug) && canEnterZone(sessionAccess(), targetSlug)
              ? 'Carte fermée : ce lieu n\'est pas ouvert pour le moment.'
              : 'Réservé : tu n\'as pas accès à ce lieu.';
            EventBus.emit('ui:toast', { message, tone: 'info' });
          }
          return;
        }
        console.log(`[ZoneScene] portail declenche: "${this.portalSpawnNames.get(portal.id) ?? portal.id}" → ${portal.targetZoneId}`);
        this.triggerZoneTransition(portal.targetZoneId, this.portalSpawnNames.get(portal.id));
        return;
      }
    }
    this.reservedPortalId = null;
  }

  /**
   * Invite trottinette : « Descendre » au-dessus de l'avatar quand on roule,
   * sinon « Monter » au-dessus de la trottinette libre la plus proche. Cachée
   * quand une porte est à portée (la touche d'action ouvre alors la porte).
   */
  private checkScooters(): void {
    if (!this.localPlayer || !this.scooters) return;
    const { x, y } = this.localPlayer;
    let mode: 'mount' | 'dismount' | 'sit' | 'stand' | 'board' | 'notice' | null = null;
    let anchor: { x: number; y: number } | null = null;
    this.nearbyScooter = null;
    this.nearbySeat = null;
    this.nearbyBoard = null;
    if (!this.nearbyDoorId) {
      if (this.localVehicle) {
        mode = 'dismount';
        anchor = { x, y: y - 56 };
      } else if (this.localSeat) {
        mode = 'stand';
        anchor = { x: x + SEAT_PROMPT_SIDE_X, y: y - SEAT_PROMPT_SIDE_Y };
      } else {
        this.nearbyScooter = this.scooters.nearest(x, y);
        if (this.nearbyScooter) {
          mode = 'mount';
          anchor = this.scooters.promptAnchor(this.nearbyScooter);
        } else {
          this.nearbySeat = this.nearestFreeSeat(x, y);
          if (this.nearbySeat) {
            mode = 'sit';
            anchor = { x: this.nearbySeat.x + SEAT_PROMPT_SIDE_X, y: this.nearbySeat.y - SEAT_PROMPT_SIDE_Y };
          } else {
            // Tableau blanc à portée : l'avatar se tient devant ou à côté (une case et demie de son pied).
            let best = BOARD_PROMPT_DISTANCE;
            for (const [itemId, b] of this.boards) {
              const dist = Math.hypot(b.x - x, Math.max(0, y - b.footY - 16, b.footY - 64 - y));
              if (dist < best) { best = dist; this.nearbyBoard = itemId; }
            }
            if (this.nearbyBoard) {
              const b = this.boards.get(this.nearbyBoard)!;
              mode = b.kind === 'notice' ? 'notice' : 'board';
              anchor = { x: b.x, y: b.y - 8 };
            }
          }
        }
      }
    }
    if (mode !== this.scooterPromptMode) {
      this.scooterPromptMode = mode;
      this.lastScooterPromptPos = null;
      if (mode) EventBus.emit('scooter:prompt', { mode });
      else EventBus.emit('scooter:prompt-hidden', {});
    }
    if (!anchor) return;
    const screen = this.worldToScreen(anchor.x, anchor.y);
    const pos = { x: Math.round(screen.x), y: Math.round(screen.y) };
    if (this.lastScooterPromptPos?.x === pos.x && this.lastScooterPromptPos.y === pos.y) return;
    this.lastScooterPromptPos = pos;
    EventBus.emit('scooter:prompt-position', pos);
  }

  /** Siège libre le plus proche, à portée de l'avatar (le serveur accepte jusqu'à SEAT_REACH). */
  private nearestFreeSeat(x: number, y: number): SeatSpot | null {
    const SEAT_PROMPT_DISTANCE = 48;
    let best: SeatSpot | null = null;
    let bestDist = SEAT_PROMPT_DISTANCE;
    for (const seat of this.seats.values()) {
      const dist = Math.hypot(seat.x - x, seat.y - y);
      if (dist > bestDist) continue;
      const taken = [...this.remoteSeats.values()].some((v) => {
        const other = parseSeat(v);
        return !!other && sameSeatSpot(other, seat);
      });
      if (taken) continue;
      best = seat;
      bestDist = dist;
    }
    return best;
  }

  /** Monter sur la trottinette à portée, ou descendre — le serveur confirme (setLocalVehicle). Idem pour l'assise. */
  private triggerScooterAction(): void {
    if (this.chatInputActive) return;
    if (this.nearbyBoard && (this.scooterPromptMode === 'board' || this.scooterPromptMode === 'notice')) {
      const board = this.boards.get(this.nearbyBoard);
      if (board?.kind === 'notice') EventBus.emit('ui:notice-board-open', { itemId: board.boardId });
      else if (board) EventBus.emit('ui:board-open', { itemId: board.boardId, name: board.name });
      return;
    }
    if (this.localSeat) {
      this.standLocal(true);
      return;
    }
    if (this.nearbySeat && this.scooterPromptMode === 'sit') {
      const seat = this.nearbySeat;
      EventBus.emit('seat:sit-request', { x: seat.x, y: seat.y, direction: seat.direction, lift: seat.lift });
      return;
    }
    if (this.localVehicle) {
      EventBus.emit('scooter:dismount-request', {});
      return;
    }
    const s = this.nearbyScooter;
    if (!s) return;
    EventBus.emit('scooter:mount-request', { itemId: s.itemId, model: s.model, x: s.x, y: s.y, direction: s.direction });
  }

  /**
   * Task 5 : Verifie la proximite du joueur local avec les portes.
   * Emet door:nearby / door:left via EventBus pour afficher le badge React.
   */
  private checkDoors(): void {
    if (!this.localPlayer) return;

    const px = this.localPlayer.x;
    const py = this.localPlayer.y;
    let closestDoor: TmjDoor | null = null;
    let closestDist = Infinity;

    for (const door of this.doorZones) {
      // Distance du joueur au bord le plus proche du rectangle de la porte
      const { x: dx, y: dy, width: dw, height: dh } = door.bounds;
      const nearestX = Phaser.Math.Clamp(px, dx, dx + dw);
      const nearestY = Phaser.Math.Clamp(py, dy, dy + dh);
      const dist = Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
      if (dist < this.DOOR_INTERACT_DISTANCE && dist < closestDist) {
        closestDoor = door;
        closestDist = dist;
      }
    }

    // Salon ouvert fermé : « Toquer » à son bord, si aucune vraie porte n'est plus près.
    let closestLock: { bubbleId: string; x: number; y: number } | null = null;
    for (const bubbleId of this.openLockBarriers.keys()) {
      const b = this.bubbleBounds.get(bubbleId);
      if (!b) continue;
      const nearestX = Phaser.Math.Clamp(px, b.x, b.x + b.width);
      const nearestY = Phaser.Math.Clamp(py, b.y, b.y + b.height);
      const dist = Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
      if (dist < this.DOOR_INTERACT_DISTANCE && dist < closestDist) {
        closestDoor = null;
        closestDist = dist;
        closestLock = { bubbleId, x: nearestX, y: nearestY };
      }
    }
    if (closestLock) {
      const id = `lock:${closestLock.bubbleId}`;
      if (this.nearbyDoorId !== id || !this.nearbyDoorLocked) {
        this.nearbyDoorId = id;
        this.nearbyDoorLocked = true;
        EventBus.emit('door:nearby', { doorId: id, doorName: 'Salon', linkedBubble: closestLock.bubbleId, locked: true });
      }
      const screen = this.worldToScreen(closestLock.x, closestLock.y - 24);
      const pos = { x: Math.round(screen.x), y: Math.round(screen.y) };
      if (this.lastDoorPromptPos?.x !== pos.x || this.lastDoorPromptPos.y !== pos.y) {
        this.lastDoorPromptPos = pos;
        EventBus.emit('door:prompt-position', pos);
      }
    } else if (closestDoor) {
      // Même condition que triggerDoorAction() : la touche ferait « toquer ».
      const locked = this.isDoorLockedForLocalPlayer(closestDoor);
      if (this.nearbyDoorId !== closestDoor.id || this.nearbyDoorLocked !== locked) {
        this.nearbyDoorId = closestDoor.id;
        this.nearbyDoorLocked = locked;
        EventBus.emit('door:nearby', {
          doorId: closestDoor.id,
          doorName: closestDoor.name,
          linkedBubble: closestDoor.linkedBubble,
          locked,
        });
      }
      this.emitDoorPromptPosition(closestDoor);
    } else if (this.nearbyDoorId !== null) {
      this.nearbyDoorId = null;
      this.nearbyDoorLocked = false;
      this.lastDoorPromptPos = null;
      EventBus.emit('door:left', {});
    }
    this.updateDoorGlow();
  }

  /**
   * Murs invisibles des salons ouverts fermés : un par bulle verrouillée sans
   * porte, quand le joueur local n'en est ni propriétaire, ni invité, ni déjà
   * dedans. Le serveur refuse de toute façon l'entrée dans la bulle
   * (ZoneRoom.handleMove) : sans ce mur, l'avatar entrait sans rejoindre la discussion.
   */
  private syncOpenLockBarriers(): void {
    const wanted = new Set<string>();
    if (this.localPlayer) {
      const here = useGameStore.getState().localBubbleId;
      for (const bubbleId of this.lockedBubbleIds) {
        if (bubbleId === here || !this.bubbleBounds.has(bubbleId)) continue;
        if (this.doorZones.some((d) => d.resolvedBubbleId === bubbleId)) continue;
        if (this.isLocalPlayerBubbleOwner(bubbleId) || this.allowedBubbleIds.has(bubbleId)) continue;
        wanted.add(bubbleId);
      }
      // Salles réservées par un accès « en plus » sans porte : même mur.
      for (const bubbleId of this.bubbleBounds.keys()) {
        if (bubbleId === here || !this.isRoomReservedForLocalPlayer(bubbleId)) continue;
        if (this.doorZones.some((d) => d.resolvedBubbleId === bubbleId)) continue;
        wanted.add(bubbleId);
      }
    }
    for (const [bubbleId, body] of this.openLockBarriers) {
      if (!wanted.has(bubbleId)) { body.destroy(); this.openLockBarriers.delete(bubbleId); }
    }
    for (const bubbleId of wanted) {
      if (this.openLockBarriers.has(bubbleId)) continue;
      const b = this.bubbleBounds.get(bubbleId)!;
      const body = this.physics.add.staticImage(b.x + b.width / 2, b.y + b.height / 2, '__DEFAULT');
      body.setDisplaySize(b.width, b.height);
      body.setVisible(false);
      body.refreshBody();
      this.physics.add.collider(this.localPlayer!, body);
      this.openLockBarriers.set(bubbleId, body);
    }
  }

  /**
   * La porte mène-t-elle à une bulle verrouillée pour le joueur local (la
   * touche ferait « toquer ») ? Seule source de vérité, partagée par
   * triggerDoorAction() et l'invite DoorPrompt (door:nearby.locked).
   */
  private isDoorLockedForLocalPlayer(door: TmjDoor): boolean {
    const linked = door.resolvedBubbleId;
    if (linked && this.isRoomReservedForLocalPlayer(linked)) return true;
    if (!linked || !this.lockedBubbleIds.has(linked)) return false;
    return !this.isLocalPlayerBubbleOwner(linked) && !this.allowedBubbleIds.has(linked);
  }

  /**
   * Salle réservée par un accès « en plus » que la personne n'a pas (`xr` du
   * jeton), ou revendiquée par un duo dont elle ne fait pas partie : le
   * serveur refuse l'entrée.
   */
  private isRoomReservedForLocalPlayer(bubbleId: string): boolean {
    return !canEnterRoom(sessionAccess(), bubbleId) || this.isRoomClaimedByOthers(bubbleId);
  }

  private isRoomClaimedByOthers(bubbleId: string): boolean {
    const { userId, role } = sessionInfo();
    return !canUseClaimedRoom(useRoomClaimsStore.getState().claims.get(bubbleId), userId, role);
  }

  private isLocalPlayerBubbleOwner(bubbleId: string): boolean {
    const owners = useGameStore.getState().lockedBubbleOwners.get(bubbleId);
    return !!(owners && this.localPlayerId && owners.has(this.localPlayerId));
  }

  /**
   * Halo sur le sprite de la porte proche, retiré des autres. Revérifié à
   * chaque frame (simple lookup) : loadZonesFromDB() peut détruire et recréer
   * le sprite d'une porte DB pendant que le joueur est devant.
   */
  private updateDoorGlow(): void {
    const target = this.nearbyDoorId ? this.doorSprites.get(this.nearbyDoorId) ?? null : null;
    if (this.glowedDoor?.sprite === target) return;

    if (this.glowedDoor) {
      const { sprite, fx } = this.glowedDoor;
      if (sprite.active) sprite.postFX?.remove(fx);
      this.glowedDoor = null;
    }
    // postFX est null en rendu Canvas (Phaser.AUTO sans WebGL) : pas de halo.
    if (!target?.postFX) return;
    const fx = target.postFX.addGlow(DOOR_GLOW_COLOR, DOOR_GLOW_OUTER_STRENGTH, DOOR_GLOW_INNER_STRENGTH);
    this.glowedDoor = { sprite: target, fx };
  }

  /** Position écran (px canvas) du haut-centre de la porte, pour DoorPrompt. */
  private emitDoorPromptPosition(door: TmjDoor): void {
    const sprite = this.doorSprites.get(door.id);
    const worldX = door.bounds.x + door.bounds.width / 2;
    const worldY = sprite ? sprite.getTopCenter().y : door.bounds.y;
    const screen = this.worldToScreen(worldX, worldY);
    const x = Math.round(screen.x);
    const y = Math.round(screen.y);
    if (this.lastDoorPromptPos?.x === x && this.lastDoorPromptPos.y === y) return;
    this.lastDoorPromptPos = { x, y };
    EventBus.emit('door:prompt-position', { x, y });
  }

  /**
   * Sprint 5 : Verifie si le joueur local est dans une zone area.
   * Emet ui:area-entered / ui:area-exited via EventBus pour afficher l'iframe overlay.
   */
  private checkAreas(): void {
    if (!this.localPlayer) return;

    const px = this.localPlayer.x;
    const py = this.localPlayer.y;
    let currentArea: TmjArea | null = null;

    for (const area of this.areaZones) {
      if (area.action !== 'iframe' && area.action !== 'launch') continue;
      const { x, y, width, height } = area.bounds;
      if (px >= x && px <= x + width && py >= y && py <= y + height) {
        currentArea = area;
        break;
      }
    }

    if (currentArea) {
      if (this.insideAreaName !== currentArea.name) {
        this.insideAreaName = currentArea.name;
        if (currentArea.action === 'launch') {
          EventBus.emit('ui:area-launch', {
            name: currentArea.name,
            url: currentArea.url,
            label: currentArea.label,
          });
        } else {
          EventBus.emit('ui:area-entered', {
            name: currentArea.name,
            url: currentArea.url,
            label: currentArea.label,
          });
        }
        console.log(`[ZoneScene] area entered: "${currentArea.name}" action="${currentArea.action}"`);
      }
    } else if (this.insideAreaName !== null) {
      const exitedName = this.insideAreaName;
      this.insideAreaName = null;
      EventBus.emit('ui:area-exited', { name: exitedName });
      console.log(`[ZoneScene] area exited: "${exitedName}"`);
    }
  }

  /**
   * Sprint 11a : Vérifie si le joueur local est dans une zone desk.
   * Émet desk:entered / desk:exited via EventBus.
   */
  private checkDesks(): void {
    if (!this.localPlayer) return;
    const px = this.localPlayer.x;
    const py = this.localPlayer.y;
    let currentDesk: TmjDesk | null = null;

    for (const desk of this.deskZones) {
      const { x, y, width, height } = desk.bounds;
      if (px >= x && px <= x + width && py >= y && py <= y + height) {
        currentDesk = desk;
        break;
      }
    }

    if (currentDesk) {
      if (this.insideDeskId !== currentDesk.deskId) {
        this.insideDeskId = currentDesk.deskId;
        this.updateDimmedArea();
        // Find owner from store
        const assignments = useGameStore.getState().deskAssignments;
        const owner = assignments.find((a) => a.deskId === currentDesk!.deskId);
        EventBus.emit('desk:entered', {
          deskId: currentDesk.deskId,
          label: currentDesk.label,
          ownerId: owner?.userId ?? null,
          ownerName: owner?.displayName ?? null,
        });
        // Notify desk owner if someone arrives at their desk
        if (owner && owner.userId !== this.localPlayerId) {
          const myDesk = useGameStore.getState().localDeskId;
          if (myDesk === currentDesk.deskId) {
            const visitorName = useGameStore.getState().displayName ?? 'Quelqu\'un';
            EventBus.emit('desk:visitor-arrived', { visitorName });
          }
        }
      }
    } else if (this.insideDeskId !== null) {
      this.insideDeskId = null;
      this.updateDimmedArea();
      EventBus.emit('desk:exited', {});
    }
  }

  /**
   * Sprint 11d : Vérifie si le joueur local est dans une zone podium.
   * Émet podium:entered / podium:exited via EventBus.
   */
  private checkPodium(): void {
    if (!this.localPlayer) return;
    const px = this.localPlayer.x;
    const py = this.localPlayer.y;
    let currentPodium: TmjPodium | null = null;

    for (const podium of this.podiumZones) {
      const { x, y, width, height } = podium.bounds;
      if (px >= x && px <= x + width && py >= y && py <= y + height) {
        currentPodium = podium;
        break;
      }
    }

    if (currentPodium) {
      if (this.insidePodiumName !== currentPodium.name) {
        this.insidePodiumName = currentPodium.name;
        EventBus.emit('podium:entered', { name: currentPodium.name });
      }
    } else if (this.insidePodiumName !== null) {
      this.insidePodiumName = null;
      EventBus.emit('podium:exited', {});
    }
  }

  /**
   * Sprint 11a : Charge les assignations de bureaux depuis l'API et affiche les labels.
   */
  private loadDeskAssignments(): void {
    if (!this.zoneConfig) return;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    const zoneSlug = this.zoneConfig.slug;
    const tenantSlug = getTenantSlug();

    fetch(`${API_BASE}/world/desks/${encodeURIComponent(zoneSlug)}`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        'X-Tenant-ID': tenantSlug,
      },
    })
      .then((res) => res.json())
      .then((assignments: Array<{ deskId: string; userId: string; displayName: string }>) => {
        EventBus.emit('desk:assignments-loaded', { assignments });
        // Check if local user has a desk
        const localUserId = this.localPlayerId;
        const myAssignment = assignments.find((a) => a.userId === localUserId);
        if (myAssignment) {
          useGameStore.getState().setLocalDeskId(myAssignment.deskId);
        }
        // Render labels on desk zones
        for (const desk of this.deskZones) {
          const assignment = assignments.find((a) => a.deskId === desk.deskId);
          const labelText = assignment
            ? `Bureau de ${assignment.displayName}`
            : desk.label;
          const cx = desk.bounds.x + desk.bounds.width / 2;
          const cy = desk.bounds.y - 8;
          const text = this.add.text(cx, cy, labelText, {
            fontSize: '10px',
            color: assignment ? '#4ade80' : '#888888',
            fontFamily: 'system-ui, sans-serif',
            align: 'center',
          });
          text.setOrigin(0.5, 1);
          text.setDepth(DEPTH_DESK_LABEL);
          this.deskLabels.set(desk.deskId, text);
        }
      })
      .catch((err) => console.warn('[ZoneScene] desk assignments load failed:', err));
  }

  /**
   * Sprint 11d : Dessine un spotlight doré autour du sprite du speaker.
   */
  private drawPodiumSpotlight(): void {
    if (!this.podiumSpotlight) return;
    this.podiumSpotlight.clear();

    const speakerId = useGameStore.getState().podiumSpeakerId;
    if (!speakerId) return;

    // Find speaker sprite
    let speakerSprite: CompositeAvatar | null = null;
    if (speakerId === this.localPlayerId) {
      speakerSprite = this.localPlayer;
    } else {
      this.remotePlayerSprites.forEach((sprite, sessionId) => {
        if (this.sessionIdToUserId.get(sessionId) === speakerId) {
          speakerSprite = sprite;
        }
      });
    }
    if (!speakerSprite) return;

    // Golden spotlight circle
    this.podiumSpotlight.lineStyle(2, 0xffd700, 0.8);
    this.podiumSpotlight.fillStyle(0xffd700, 0.15);
    this.podiumSpotlight.fillCircle(speakerSprite.x, speakerSprite.y, 24);
    this.podiumSpotlight.strokeCircle(speakerSprite.x, speakerSprite.y, 24);
  }

  /**
   * Desactive/reactive les collisions sur les tuiles couvertes par le rectangle de la porte.
   * Permet au joueur de traverser la porte apres avoir appuye sur E.
   * La collision est reactualisee quand le joueur s'eloigne.
   */
  private toggleDoorCollision(door: TmjDoor): void {
    if (!this.tilemap) return;

    const { x, y, width, height } = door.bounds;
    const tileW = this.tilemap.tileWidth;
    const tileH = this.tilemap.tileHeight;
    const startCol = Math.floor(x / tileW);
    const endCol   = Math.ceil((x + width) / tileW);
    const startRow = Math.floor(y / tileH);
    const endRow   = Math.ceil((y + height) / tileH);

    console.log(`[ZoneScene] door action — porte: "${door.name}" cols=${startCol}-${endCol} rows=${startRow}-${endRow}`);

    let tilesToggled = 0;
    for (const layer of this.collisionLayers) {
      for (let row = startRow; row < endRow; row++) {
        for (let col = startCol; col < endCol; col++) {
          const tile = layer.getTileAt(col, row);
          if (tile && tile.properties?.collides) {
            tile.setCollision(false);
            tilesToggled++;
          }
        }
      }
    }

    console.log(`[ZoneScene] tiles desactivees: ${tilesToggled}`);

    // Porte créée via l'éditeur (map_zones) — pas de tuile, corps de
    // collision synthétique dédié (voir doorCollisionBodies).
    const body = this.doorCollisionBodies.get(door.id);
    if (body?.body) body.body.enable = false;

    // Refermer après 1,5 s, mais jamais sur l'avatar : tant que ses pieds sont
    // dans l'embrasure, la porte reste ouverte (sinon le corps se réactivait
    // par-dessus lui et il traversait ensuite la porte fermée).
    const inDoorway = (): boolean => {
      const feet = this.localPlayer?.body as Phaser.Physics.Arcade.Body | undefined;
      if (!feet) return false;
      return feet.right > x && feet.x < x + width && feet.bottom > y && feet.y < y + height;
    };
    const closeWhenClear = (): void => {
      if (inDoorway()) { this.time.delayedCall(250, closeWhenClear); return; }
      for (const layer of this.collisionLayers) {
        for (let row = startRow; row < endRow; row++) {
          for (let col = startCol; col < endCol; col++) {
            const tile = layer.getTileAt(col, row);
            if (tile && tile.properties?.collides) {
              tile.setCollision(true);
            }
          }
        }
      }
      if (body?.body) body.body.enable = true;
      console.log(`[ZoneScene] collisions porte "${door.name}" reactualisees`);
      this.doorSprites.get(door.id)?.close();
    };
    this.time.delayedCall(1500, closeWhenClear);
  }

  /** Ascenseurs de la carte, créés au premier chargement des zones (détruits avec la scène). */
  private ensureElevators(): Elevators {
    if (this.elevators) return this.elevators;
    const elevators = new Elevators(
      this,
      this.zoneConfig!.slug,
      (bottomY) => DEPTH_Y_BASE + bottomY / DEPTH_Y_SCALE,
      (mapSlug, spawnName, zoneId, direction) => this.boardElevator(zoneId, direction, () =>
        this.triggerZoneTransition(`${this.zoneConfig!.tenantId}:${mapSlug}`, spawnName)),
    );
    this.elevators = elevators;
    this.sceneCleanups.push(() => {
      elevators.destroy();
      if (this.elevators === elevators) this.elevators = null;
    });
    return elevators;
  }

  /**
   * Étage choisi : l'avatar s'immobilise devant les portes ouvertes et demande
   * une place dans la cabine (elevator:slot). Cabine pleine : il prend le
   * trajet suivant. Pas de réponse (temps réel coupé) : place 0.
   */
  private boardElevator(zoneId: string, direction: 'up' | 'down', then: () => void): void {
    const player = this.localPlayer;
    if (!player || !this.elevators) { then(); return; }
    this.elevatorWalking = true;
    this.clearMoveTarget();
    (player.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
    this.elevators.hold(zoneId);
    this.pendingElevatorBoard = { zoneId, direction, then, timer: null, warned: false };
    this.requestElevatorPlace();
  }

  private requestElevatorPlace(): void {
    const pending = this.pendingElevatorBoard;
    if (!pending) return;
    EventBus.emit('elevator:board-request', { zoneId: pending.zoneId, direction: pending.direction });
    pending.timer = this.time.delayedCall(ELEVATOR_REPLY_TIMEOUT_MS, () => this.onElevatorSlot(pending.zoneId, 0));
  }

  /** Place donnée par le serveur : l'avatar entre dans la cabine. */
  onElevatorSlot(zoneId: string, slot: number): void {
    const pending = this.pendingElevatorBoard;
    if (!pending || pending.zoneId !== zoneId) return;
    pending.timer?.remove();
    this.pendingElevatorBoard = null;
    const cabin = this.elevators?.cabin(zoneId, slot);
    if (!cabin) { pending.then(); return; }
    this.walkIntoElevator(zoneId, cabin, pending.direction, pending.then);
  }

  /** Cabine pleine : nouvel essai au trajet suivant, portes toujours ouvertes. */
  onElevatorFull(zoneId: string): void {
    const pending = this.pendingElevatorBoard;
    if (!pending || pending.zoneId !== zoneId) return;
    pending.timer?.remove();
    if (!pending.warned) {
      pending.warned = true;
      EventBus.emit('ui:toast', { message: 'Ascenseur plein : tu prends le suivant.', tone: 'info' });
    }
    pending.timer = this.time.delayedCall(ELEVATOR_BOARDING_MS, () => this.requestElevatorPlace());
  }

  /**
   * L'avatar monte dans la cabine à sa place (devant les portes ouvertes), se
   * retourne, puis les portes se ferment sur lui (il passe derrière quand elles
   * sont jointes) avant le fondu. Le mur sous l'ascenseur bloque : corps coupé
   * pendant le pas. D'autres qui entrent encore gardent les portes ouvertes.
   */
  private walkIntoElevator(zoneId: string, cabin: ElevatorCabin, direction: 'up' | 'down', then: () => void): void {
    const player = this.localPlayer;
    if (!player) { this.elevators?.release(zoneId, direction); then(); return; }
    const body = player.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(0, 0);
    body.enable = false;
    this.localDepthOverride = cabin.door.depth + 0.005;
    this.lastDirection = 'up';
    player.playCompositeAnimation('walk_up');
    this.tweens.add({
      targets: player, x: cabin.x, y: cabin.y, duration: ELEVATOR_STEP_MS, ease: 'Sine.easeInOut',
      onComplete: () => {
        this.lastDirection = 'down';
        player.playCompositeAnimation('idle_down');
        this.elevators?.release(zoneId, direction);
        this.whenElevatorShut(cabin.door, () => { this.localDepthOverride = cabin.door.depth - 0.005; });
        this.time.delayedCall(ELEVATOR_DOORS_MS, then);
      },
    });
  }

  /**
   * Arrivée par l'ascenseur : l'avatar attend dans la cabine, portes fermées,
   * que le temps réel donne les avatars déjà là (room:state), pour prendre une
   * place libre du palier ; puis les portes s'ouvrent pour tout le monde.
   */
  private arriveByElevator(zoneId: string): void {
    const player = this.localPlayer;
    const center = this.elevators?.cabin(zoneId);
    if (!player || !center) return;
    const origin = { x: player.x, y: player.y };
    this.elevatorWalking = true;
    (player.body as Phaser.Physics.Arcade.Body).enable = false;
    // Position annoncée au serveur à la connexion : les autres le voient d'abord dans la cabine.
    player.setPosition(center.x, center.y);
    this.localDepthOverride = center.door.depth - 0.005;
    this.lastDirection = 'down';
    player.playCompositeAnimation('idle_down');
    const timer = this.time.delayedCall(ELEVATOR_ARRIVAL_WAIT_MS, () => this.onRoomStateApplied());
    this.pendingElevatorArrival = { zoneId, origin, timer };
  }

  /** room:state appliqué (avatars des autres connus) : l'avatar qui attend dans la cabine sort. */
  onRoomStateApplied(): void {
    const pending = this.pendingElevatorArrival;
    if (!pending) return;
    this.pendingElevatorArrival = null;
    pending.timer.remove();
    const others = [...this.remotePlayerSprites.values()].map((s) => ({ x: s.x, y: s.y }));
    const { grid } = this.buildWalkGrid();
    const blocked = (x: number, y: number) => {
      const cx = Math.floor(x / grid.tileSize);
      const cy = Math.floor(y / grid.tileSize);
      return cx < 0 || cy < 0 || cx >= grid.width || cy >= grid.height || grid.blocked[cy * grid.width + cx] === 1;
    };
    const landing = chooseLandingSpot(pending.origin, others, blocked);
    const spot = ELEVATOR_LANDING_SPOTS[landing]!;
    const target = { x: pending.origin.x + spot.dx, y: pending.origin.y + spot.dy };
    const cabin = this.elevators?.cabin(pending.zoneId, cabinSpotForLanding(landing));
    if (!cabin) { this.finishElevatorArrival(target); return; }
    EventBus.emit('elevator:arrive-request', { zoneId: pending.zoneId, slot: landing, x: target.x, y: target.y });
    this.walkOutOfElevator(pending.zoneId, cabin, target);
  }

  /** Les portes s'ouvrent sur l'avatar, qui sort sur sa place du palier. */
  private walkOutOfElevator(zoneId: string, cabin: ElevatorCabin, target: { x: number; y: number }): void {
    const player = this.localPlayer;
    if (!player) return;
    player.setPosition(cabin.x, cabin.y);
    this.elevators?.hold(zoneId);
    this.time.delayedCall(ELEVATOR_DOORS_MS / 2, () => { this.localDepthOverride = cabin.door.depth + 0.005; });
    this.time.delayedCall(ELEVATOR_DOORS_MS, () => {
      player.playCompositeAnimation('walk_down');
      this.tweens.add({
        targets: player, x: target.x, y: target.y, duration: ELEVATOR_STEP_MS, ease: 'Sine.easeInOut',
        onComplete: () => {
          this.finishElevatorArrival(target);
          this.time.delayedCall(ELEVATOR_STEP_MS, () => this.elevators?.release(zoneId));
        },
      });
    });
  }

  private finishElevatorArrival(target: { x: number; y: number }): void {
    const player = this.localPlayer;
    if (!player) return;
    const body = player.body as Phaser.Physics.Arcade.Body;
    player.setPosition(target.x, target.y);
    player.playCompositeAnimation('idle_down');
    body.reset(target.x, target.y);
    body.enable = true;
    this.localDepthOverride = null;
    this.elevatorWalking = false;
    EventBus.emit('player:moved', { x: target.x, y: target.y, direction: 'down', animation: 'idle' });
    this.ensureLocalPlayerOnFreeCell();
  }

  /** `fn` quand les portes sont jointes (ce qui est dans la cabine ne se voit plus), 4 s au plus. */
  private whenElevatorShut(door: ElevatorDoor, fn: () => void): void {
    let tries = 0;
    const check = () => {
      if (!door.active) return;
      if (door.isShut() || ++tries > 40) { fn(); return; }
      this.time.delayedCall(100, check);
    };
    this.time.delayedCall(100, check);
  }

  // ── Les autres dans l'ascenseur (relayés par ZoneRoom, lib/elevatorRules.ts) ──

  /** Quelqu'un d'autre a choisi un étage : portes ouvertes, il entre à sa place. */
  remoteElevatorBoarded(sessionId: string, zoneId: string, slot: number, direction: 'up' | 'down'): void {
    const sprite = this.remotePlayerSprites.get(sessionId);
    const cabin = this.elevators?.cabin(zoneId, slot);
    if (!sprite || !cabin || !this.elevators) return;
    this.startRemoteElevatorScript(sessionId, ELEVATOR_REMOTE_SCRIPT_MS);
    this.elevators.hold(zoneId);
    this.time.delayedCall(ELEVATOR_STEP_MS, () => this.elevators?.release(zoneId, direction));
    this.remoteDepthOverrides.set(sessionId, cabin.door.depth + 0.005);
    sprite.playCompositeAnimation('walk_up');
    this.tweens.add({
      targets: sprite, x: cabin.x, y: cabin.y, duration: ELEVATOR_STEP_MS, ease: 'Sine.easeInOut',
      onComplete: () => {
        sprite.playCompositeAnimation('idle_down');
        this.whenElevatorShut(cabin.door, () => {
          if (this.remoteElevatorScripts.has(sessionId)) this.remoteDepthOverrides.set(sessionId, cabin.door.depth - 0.005);
        });
      },
    });
  }

  /** Quelqu'un arrive par l'ascenseur : il attend dans la cabine, les portes s'ouvrent, il sort à sa place. */
  remoteElevatorArriving(sessionId: string, zoneId: string, slot: number, x: number, y: number): void {
    const sprite = this.remotePlayerSprites.get(sessionId);
    const cabin = this.elevators?.cabin(zoneId, cabinSpotForLanding(slot));
    if (!sprite || !cabin || !this.elevators) return;
    this.startRemoteElevatorScript(sessionId, ELEVATOR_DOORS_MS + ELEVATOR_STEP_MS + 50);
    sprite.setPosition(cabin.x, cabin.y);
    sprite.playCompositeAnimation('idle_down');
    this.remoteDepthOverrides.set(sessionId, cabin.door.depth - 0.005);
    this.elevators.hold(zoneId);
    this.time.delayedCall(ELEVATOR_DOORS_MS + 2 * ELEVATOR_STEP_MS, () => this.elevators?.release(zoneId));
    this.time.delayedCall(ELEVATOR_DOORS_MS / 2, () => {
      if (this.remoteElevatorScripts.has(sessionId)) this.remoteDepthOverrides.set(sessionId, cabin.door.depth + 0.005);
    });
    this.time.delayedCall(ELEVATOR_DOORS_MS, () => {
      if (this.remotePlayerSprites.get(sessionId) !== sprite) return;
      sprite.playCompositeAnimation('walk_down');
      this.tweens.add({
        targets: sprite, x, y, duration: ELEVATOR_STEP_MS, ease: 'Sine.easeInOut',
        onComplete: () => sprite.playCompositeAnimation('idle_down'),
      });
    });
  }

  private startRemoteElevatorScript(sessionId: string, durationMs: number): void {
    this.remoteElevatorScripts.get(sessionId)?.end.remove();
    const end = this.time.delayedCall(durationMs, () => this.endRemoteElevatorScript(sessionId));
    this.remoteElevatorScripts.set(sessionId, { latest: null, end });
  }

  /** Fin de la scène de l'ascenseur : la position reçue entre-temps du serveur s'applique. */
  private endRemoteElevatorScript(sessionId: string): void {
    const script = this.remoteElevatorScripts.get(sessionId);
    if (!script) return;
    this.remoteElevatorScripts.delete(sessionId);
    script.end.remove();
    this.remoteDepthOverrides.delete(sessionId);
    if (script.latest) this.upsertRemotePlayer(script.latest);
  }

  private triggerZoneTransition(targetZoneId: string, spawnName?: string): void {
    if (this.transitionCooldown) return;

    this.transitionCooldown = true;
    const fromZoneId = this.zoneConfig!.id;
    // Fondu au noir, puis changement de carte ; la suivante s'ouvre en fondu
    // (finishCreate). L'avatar s'arrête pendant le fondu.
    this.portalFading = true;
    this.clearMoveTarget();
    fadeInOnArrival = true;
    const camera = this.cameras.main;
    camera.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      EventBus.emit('transition:started', { fromZoneId, toZoneId: targetZoneId, spawnName });
    });
    camera.fadeOut(PORTAL_FADE_MS, 0, 0, 0);

    this.time.delayedCall(this.TRANSITION_DEBOUNCE_MS, () => {
      this.transitionCooldown = false;
    });
  }

  /**
   * Parcourt le layer "zones" du TMJ pour extraire :
   *   - type="spawn"  → spawn points nommes
   *   - type="portal" → portails inter-zones (targetSlug + spawnId)
   *   - type="door"   → portes interactives (linkedBubble)
   *   - type="start"  → spawn points legacy (Copyhouse)
   *   - exitUrl prop  → portails legacy (Copyhouse)
   */
  private parseZoneObjects(map: Phaser.Tilemaps.Tilemap): void {
    if (!this.zoneConfig) return;

    const tenantId = this.zoneConfig.tenantId;

    // map.objects contient les ObjectLayer (objectgroup dans le TMJ).
    // map.layers ne contient que les TilemapLayer (tilelayer) — les object layers n'y sont pas.
    const objectLayers = map.objects ?? [];
    console.log(`[ZoneScene] parseZoneObjects — ${objectLayers.length} object layers trouvés:`,
      objectLayers.map(ol => `"${ol.name}" (${ol.objects.length} objs)`));

    for (const objectLayer of objectLayers) {
      for (const obj of objectLayer.objects) {
        // Phaser 3.60+ : Tiled "type" peut etre dans obj.type OU obj.class
        const objType = ((obj as unknown as { class?: string }).class ?? obj.type ?? '').toLowerCase();

        // ── Task 2 : Spawns (type="spawn") ────────────────────
        if (objType === 'spawn' && obj.name) {
          // Centre du rectangle spawn
          const sx = (obj.x ?? 0) + (obj.width ?? 0) / 2;
          const sy = (obj.y ?? 0) + (obj.height ?? 0) / 2;
          this.spawnPoints.set(obj.name, { x: sx, y: sy });
          console.log(`[ZoneScene] Spawn "${obj.name}" → (${sx}, ${sy})`);
        }

        // ── Task 4 : Portails (type="portal") ─────────────────
        if (objType === 'portal') {
          const targetSlug = getTiledProperty(obj.properties, 'targetSlug') as string | undefined;
          const spawnId = getTiledProperty(obj.properties, 'spawnId') as string | undefined;
          if (!targetSlug) {
            console.warn(`[ZoneScene] Portal "${obj.name}" sans targetSlug`);
            continue;
          }

          const portalId = `tmj-portal-${obj.name ?? obj.id}`;
          this.zoneConfig.portals.push({
            id: portalId,
            targetZoneId: `${tenantId}:${targetSlug}`,
            triggerBounds: {
              x:      obj.x ?? 0,
              y:      obj.y ?? 0,
              width:  obj.width ?? 0,
              height: obj.height ?? 0,
            },
            hysteresisBuffer: this.HYSTERESIS_BUFFER_PX,
          });
          if (spawnId) {
            this.portalSpawnNames.set(portalId, spawnId);
          }
          console.log(`[ZoneScene] Portal TMJ "${obj.name}" → slug="${targetSlug}" spawn="${spawnId ?? ''}" rect=(${obj.x},${obj.y},${obj.width},${obj.height})`);
        }

        // ── Task 5 : Portes (type="door") ──────────────────────
        if (objType === 'door') {
          const linkedBubble = getTiledProperty(obj.properties, 'linkedBubble') as string | undefined;
          if (!linkedBubble) {
            console.warn(`[ZoneScene] Door "${obj.name}" sans linkedBubble`);
            continue;
          }
          const doorSprite = (getTiledProperty(obj.properties, 'doorSprite') as string | undefined) ?? null;
          this.doorZones.push({
            id: `door-${obj.name ?? obj.id}`,
            name: obj.name ?? '',
            bounds: {
              x:      obj.x ?? 0,
              y:      obj.y ?? 0,
              width:  obj.width ?? 0,
              height: obj.height ?? 0,
            },
            linkedBubble,
            // Bulles TMJ : id === name, résolution triviale par défaut —
            // écrasée si besoin par loadZonesFromDB() (voir Fix I-2 / Task 19).
            resolvedBubbleId: linkedBubble,
            doorSprite,
          });
          console.log(`[ZoneScene] Door "${obj.name}" → linkedBubble="${linkedBubble}" doorSprite="${doorSprite}" rect=(${obj.x},${obj.y},${obj.width},${obj.height})`);
        }

        // ── Zones area (type="area") — dispatch par action ──────
        if (objType === 'area') {
          const action = getTiledProperty(obj.properties, 'action') as string | undefined;
          const url = getTiledProperty(obj.properties, 'url') as string | undefined;
          const label = getTiledProperty(obj.properties, 'label') as string | undefined;
          console.log('[ZoneScene] Area raw props:', JSON.stringify(obj.properties), 'name:', obj.name);

          // ── Sprint 11a : Bureaux (action="desk") ──────
          if (action === 'desk') {
            const deskId = getTiledProperty(obj.properties, 'deskId') as string | undefined;
            if (deskId) {
              this.deskZones.push({
                id: `tmj-desk-${obj.name ?? obj.id}`,
                deskId,
                name: obj.name ?? '',
                bounds: {
                  x:      obj.x ?? 0,
                  y:      obj.y ?? 0,
                  width:  obj.width ?? 0,
                  height: obj.height ?? 0,
                },
                label: label ?? `Bureau ${deskId}`,
              });
              console.log(`[ZoneScene] Desk "${deskId}" → rect=(${obj.x},${obj.y},${obj.width},${obj.height})`);
            } else {
              console.warn(`[ZoneScene] Area "${obj.name}" action="desk" sans deskId`);
            }
            continue;
          }

          // ── Sprint 11d : Podium (action="podium") ─────
          if (action === 'podium') {
            this.podiumZones.push({
              name: obj.name ?? '',
              bounds: {
                x:      obj.x ?? 0,
                y:      obj.y ?? 0,
                width:  obj.width ?? 0,
                height: obj.height ?? 0,
              },
            });
            console.log(`[ZoneScene] Podium "${obj.name}" → rect=(${obj.x},${obj.y},${obj.width},${obj.height})`);
            continue;
          }

          // ── Sprint 5 : iframe / launch areas ──────────
          if (!url || !action) {
            console.warn(`[ZoneScene] Area "${obj.name}" sans url ou action`);
            continue;
          }
          this.areaZones.push({
            name: obj.name ?? '',
            bounds: {
              x:      obj.x ?? 0,
              y:      obj.y ?? 0,
              width:  obj.width ?? 0,
              height: obj.height ?? 0,
            },
            url,
            action,
            label: label ?? obj.name ?? '',
          });
          console.log(`[ZoneScene] Area "${obj.name}" → action="${action}" url="${url}" rect=(${obj.x},${obj.y},${obj.width},${obj.height})`);
        }

        // ── Legacy : Portails exitUrl (Copyhouse) ─────────────
        const exitUrl = getTiledProperty(obj.properties, 'exitUrl') as string | undefined;
        if (exitUrl) {
          const [filepart, spawnName] = exitUrl.split('#');
          const tmxName = (filepart ?? '').replace(/\.tmx$/, '');
          const targetSlug = TMX_TO_SLUG[tmxName];
          if (!targetSlug) {
            console.warn(`[ZoneScene] exitUrl "${exitUrl}" — slug inconnu pour "${tmxName}"`);
            continue;
          }

          const portalId = `tmj-${obj.name ?? obj.id}`;
          this.zoneConfig.portals.push({
            id: portalId,
            targetZoneId: `${tenantId}:${targetSlug}`,
            triggerBounds: {
              x:      obj.x ?? 0,
              y:      obj.y ?? 0,
              width:  obj.width ?? 0,
              height: obj.height ?? 0,
            },
            hysteresisBuffer: this.HYSTERESIS_BUFFER_PX,
          });
          if (spawnName) {
            this.portalSpawnNames.set(portalId, spawnName);
          }
        }

        // ── Legacy : Spawn points (type="start") ──────────────
        if (obj.type === 'start' && obj.name) {
          this.spawnPoints.set(obj.name, { x: obj.x ?? 0, y: obj.y ?? 0 });
        }
      }
    }
  }

  // ── Zoom carte ─────────────────────────────────────────────

  /** Touches + / − : niveau suivant (le zoom molette peut être entre deux niveaux). */
  private stepZoom(direction: 1 | -1): void {
    const level = nextZoomLevel(ZOOM_LEVELS, this.cameras.main.zoom, direction);
    if (level !== null) this.applyZoom(level);
  }

  private applyZoom(level: number): void {
    const zoom = Math.min(ZOOM_MAX, Math.max(level, this.minZoomForMap()));
    const camera = this.cameras.main;
    camera.setZoom(zoom);
    // Limites élargies de la place prise par l'interface (barres du haut et du
    // bas, panneau latéral) : arrivé au bord de la carte, l'avatar reste visible
    // au lieu de passer sous les boutons. Carte plus petite que la place
    // libre : centrée dans cette place.
    const inset = this.hudInsets();
    const axis = (map: number, view: number, before: number, after: number): [number, number] => {
      const free = view - before - after;
      if (map >= free) return [-before, map + before + after];
      return [-before - (free - map) / 2, view];
    };
    const [x, w] = axis(this.mapWidth, camera.width / zoom, inset.left / zoom, inset.right / zoom);
    const [y, h] = axis(this.mapHeight, camera.height / zoom, inset.top / zoom, inset.bottom / zoom);
    camera.setBounds(x, y, w, h);
    // L'avatar suivi (ou la salle cadrée) est centré dans la place libre, pas au
    // milieu de l'écran. Phaser centre la caméra sur « cible - décalage » : le
    // décalage va donc du côté opposé au panneau. Il était inversé jusqu'au
    // 2026-11-03 : panneau ouvert, l'avatar et la salle de réunion cadrée
    // passaient en partie dessous (vu en coaching).
    camera.setFollowOffset((inset.left - inset.right) / 2 / zoom, (inset.top - inset.bottom) / 2 / zoom);
    this.lastMinZoom = this.minZoomForMap();
    this.updateRoomFraming();
    if (useGameStore.getState().zoomLevel !== zoom) useGameStore.getState().setZoomLevel(zoom);
  }

  /**
   * Dans une salle de réunion qui tient dans la place libre (entre la rangée
   * de vignettes ou les boutons du haut, la barre du bas et le panneau
   * latéral), la caméra cadre la salle entière plutôt que l'avatar : sans ça,
   * le haut de la salle passait sous les vignettes. Salle trop grande pour le
   * zoom choisi, autre bulle, hors salle : retour au suivi de l'avatar.
   * Sans effet pendant un pan manuel (cameraDetached), repris à sa fin.
   */
  private updateRoomFraming(): void {
    const camera = this.cameras?.main;
    if (!camera || !this.localPlayer) return;
    const { localBubbleId, localBubbleType } = useGameStore.getState();
    const room = localBubbleId && localBubbleType === 'meeting-room' ? this.bubbleBounds.get(localBubbleId) : undefined;
    let frame = false;
    if (room) {
      const inset = this.hudInsets();
      const margin = 16; // px d'écran autour de la salle
      const freeW = (camera.width - inset.left - inset.right - 2 * margin) / camera.zoom;
      const freeH = (camera.height - inset.top - inset.bottom - 2 * margin) / camera.zoom;
      frame = room.width <= freeW && room.height <= freeH;
    }
    const previous = this.roomCameraTarget;
    if (frame && room) {
      const cx = room.x + room.width / 2;
      const cy = room.y + room.height / 2;
      if (!this.roomCameraTarget) this.roomCameraTarget = this.add.zone(cx, cy, 1, 1);
      else this.roomCameraTarget.setPosition(cx, cy);
    } else {
      this.roomCameraTarget = null;
    }
    if (!this.cameraDetached) this.followCameraTarget(true);
    if (previous && previous !== this.roomCameraTarget) previous.destroy();
  }

  /**
   * Suivi caméra : la salle cadrée s'il y en a une, sinon l'avatar. startFollow
   * de Phaser replace la caméra d'un coup sur sa cible : en changeant de cible,
   * glissé d'abord jusqu'à elle (décalage de suivi compris), suivi ensuite.
   */
  private followCameraTarget(glide = false): void {
    const camera = this.cameras.main;
    const target = this.roomCameraTarget ?? this.localPlayer;
    if (!target) return;
    if (this.cameraFollowing === target) return;
    this.cameraFollowing = target;
    if (!glide) {
      camera.startFollow(target, true, 0.12, 0.12);
      return;
    }
    camera.stopFollow();
    camera.pan(target.x - camera.followOffset.x, target.y - camera.followOffset.y, 400, 'Sine.easeOut', true,
      (_camera: Phaser.Cameras.Scene2D.Camera, progress: number) => {
        // Cible changée pendant le glissé : le nouvel appel a déjà pris la main.
        if (progress === 1 && this.cameraFollowing === target) camera.startFollow(target, true, 0.12, 0.12);
      });
  }

  /** Zoom minimal au dernier recalage : la carte était-elle affichée en entier ? (onHudInsetsChanged) */
  private lastMinZoom = 0;

  /**
   * Place prise par l'interface changée (panneau latéral, vignettes vidéo) :
   * une carte affichée en entier se réajuste à la nouvelle place, sinon on
   * garde le zoom choisi.
   */
  private onHudInsetsChanged(): void {
    const camera = this.cameras.main;
    const wasFit = Math.abs(camera.zoom - this.lastMinZoom) < 1e-3;
    this.applyZoom(wasFit ? 0 : camera.zoom);
  }

  /**
   * Place prise par l'interface au-dessus du canvas, en px d'écran : rangée
   * du haut (zone, outils) ou vignettes vidéo si elles descendent plus bas,
   * barre du bas (mesurées par usePublishHudInsets), panneau latéral ouvert
   * (chat, personnes), plus 8 px de marge.
   */
  private hudInsets(): { top: number; bottom: number; left: number; right: number } {
    const mobile = window.matchMedia('(max-width: 768px)').matches;
    const css = getComputedStyle(document.documentElement);
    // --pl-side-panel : posée sur <html> par SidePanel tant qu'il est ouvert (ordinateur).
    const panel = parseFloat(css.getPropertyValue('--pl-side-panel')) || 0;
    // --pl-tiles-bottom : bas de la rangée de vignettes vidéo (VideoTiles), 0 sans vidéo.
    const tiles = parseFloat(css.getPropertyValue('--pl-tiles-bottom')) || 0;
    // --pl-hud-top / --pl-hud-bottom : place des barres, mesurée ; défauts dans ds.css.
    const hudTop = parseFloat(css.getPropertyValue('--pl-hud-top')) || (mobile ? 60 : 56);
    const hudBottom = parseFloat(css.getPropertyValue('--pl-hud-bottom')) || (mobile ? 68 : 80);
    return { top: Math.max(hudTop, tiles) + 8, bottom: hudBottom + 8, left: 0, right: mobile ? 0 : panel };
  }

  /**
   * Zoom minimal : la carte entière remplit la place libre entre les barres
   * (une salle de 15 × 10 cases s'affiche en grand, pas en vignette dans un
   * coin). Sans effet sur une carte plus grande que l'écran.
   */
  private minZoomForMap(): number {
    const camera = this.cameras.main;
    if (!this.mapWidth || !this.mapHeight) return ZOOM_MIN;
    // Sans le panneau latéral : l'ouvrir ne doit pas changer le zoom.
    const inset = this.hudInsets();
    const freeW = Math.max(1, camera.width);
    const freeH = Math.max(1, camera.height - inset.top - inset.bottom);
    const contain = Math.min(freeW / this.mapWidth, freeH / this.mapHeight);
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, contain));
  }

  // ── Indicateur visuel de parole ────────────────────────────

  private findSpriteByUserId(userId: string): CompositeAvatar | null {
    if (this.localPlayerId === userId) return this.localPlayer;
    let found: CompositeAvatar | null = null;
    this.sessionIdToUserId.forEach((uid, sid) => {
      if (uid === userId) found = this.remotePlayerSprites.get(sid) ?? null;
    });
    return found;
  }

  private updateSpeakerPings(speakerUserIds: string[]): void {
    const speakerSet = new Set(speakerUserIds);

    // Retirer les pings pour ceux qui ne parlent plus
    this.speakerPings.forEach((gfx, userId) => {
      if (!speakerSet.has(userId)) {
        this.tweens.killTweensOf(gfx);
        gfx.destroy();
        this.speakerPings.delete(userId);
      }
    });

    // Ajouter les pings pour les nouveaux speakers
    for (const userId of speakerUserIds) {
      if (this.speakerPings.has(userId)) continue;
      const sprite = this.findSpriteByUserId(userId);
      if (!sprite) continue;

      const gfx = this.add.graphics();
      gfx.setDepth(DEPTH_SPEAKER);
      this.speakerPings.set(userId, gfx);

      gfx.lineStyle(2, 0x22c55e, 0.8);
      gfx.strokeCircle(0, 0, 28);
      gfx.setPosition(sprite.x, sprite.y);

      this.tweens.add({
        targets: gfx,
        scaleX: 1.6,
        scaleY: 1.6,
        alpha: 0,
        duration: 800,
        repeat: -1,
        onRepeat: () => {
          gfx.setAlpha(0.8);
          gfx.setScale(1);
        },
      });
    }
  }

  private updateSpeakerPingPositions(): void {
    this.speakerPings.forEach((gfx, userId) => {
      const sprite = this.findSpriteByUserId(userId);
      if (sprite) gfx.setPosition(sprite.x, sprite.y);
    });
  }
}

// ── Utilitaire Tiled ──────────────────────────────────────────────────────────

function getTiledProperty(
  properties: unknown,
  name: string,
): string | number | boolean | undefined {
  if (!properties) return undefined;
  if (Array.isArray(properties)) {
    const entry = properties.find(
      (p: { name: string }) => p.name === name,
    ) as { value: string | number | boolean } | undefined;
    return entry?.value;
  }
  return (properties as Record<string, string | number | boolean>)[name];
}

// ── Utilitaire URL ────────────────────────────────────────────────────────────

export function buildZoneAssetUrls(
  tenantId: string,
  slug: string,
  avatarsUrl: string,
  tilesetNames: string[] = ['campus-base'],
): ZoneAssetUrls {
  const base = `/assets/tenants/${tenantId}/maps/${slug}`;
  return {
    tmj:      assetUrl(`${base}/map.tmj`),
    wam:      assetUrl(`${base}/map.wam`),
    tilesets: tilesetNames.map((name) => assetUrl(`${base}/tilesets/${name}.webp`)),
    sprites:  { avatars: avatarsUrl },
  };
}
